import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Seo from '../components/Seo';
import BlogManager from '../components/admin/BlogManager';
import Toast from '../components/Toast';
import CourseIcon from '../components/CourseIcon';
import logo from '../assets/logo-main.png';
import { apiBaseUrl } from '../config';
import { formatDateTime as fmtDate } from '../utils/format';
import { clearAdminSession, getAdminData, getAdminToken, setAdminSession } from '../services/adminSession';
import { adminListCourses, adminCreateCourse, adminUpdateCourse, adminDeleteCourse, adminUploadCourseImage } from '../services/courses';
import { adminListTools, adminUpdateTool, adminDeleteTool } from '../services/tools';
import { triggerSimpleTask, triggerBackfillJob, listManualJobs } from '../services/marketJobs';
import AdminMarketData from '../components/admin/AdminMarketData';
import './AdminDashboard.css';

// Status badge colours for manual jobs.
const JOB_STATUS_STYLE = {
  STARTED:             { color: '#94a3b8',       bg: 'rgba(148,163,184,0.12)' },
  RUNNING:             { color: '#3b82f6',       bg: 'rgba(59,130,246,0.12)'  },
  COMPLETED:           { color: 'var(--green)',  bg: 'rgba(0,212,170,0.12)'   },
  PARTIALLY_COMPLETED: { color: '#f5a623',       bg: 'rgba(245,166,35,0.12)'  },
  SKIPPED:             { color: '#94a3b8',       bg: 'rgba(148,163,184,0.12)' },
  FAILED:              { color: 'var(--red)',    bg: 'rgba(255,77,106,0.12)'  },
};

// Friendly labels for manual job types.
const JOB_TYPE_LABEL = {
  FETCH_QUOTES:               'Fetch Live Quotes',
  FETCH_OHLCV:                'Fetch Daily Bars',
  BACKFILL_HISTORICAL_QUOTES: 'Historical Quotes Backfill',
};

// Job types an admin can trigger from the Manual Jobs tab.
const TRIGGERABLE_JOBS = [
  { type: 'FETCH_QUOTES',               desc: 'Fetch the current price for all active stocks and update live market data.' },
  { type: 'FETCH_OHLCV',                desc: 'Fetch the daily price bars (OHLCV) for all active stocks.' },
  { type: 'BACKFILL_HISTORICAL_QUOTES', desc: 'Backfill daily OHLCV history for a date range. Re-running refreshes existing bars — no duplicates.' },
];

// One-line summary of a job's params for the jobs table.
const jobSummary = (j) => {
  const p = j.params || {};
  if (j.job_type === 'BACKFILL_HISTORICAL_QUOTES') {
    const syms = p.requested_scope === 'ALL' ? 'ALL active' : (p.symbols || []).join(', ');
    return `${syms} · ${p.start_date || '?'} → ${p.end_date || '?'} · ${p.interval || '1d'}`;
  }
  if (j.job_type === 'FETCH_OHLCV') return `period ${p.period || '5d'}`;
  if (j.job_type === 'FETCH_QUOTES') return 'all active stocks';
  return '—';
};

// Today's date as YYYY-MM-DD in the browser's local timezone (for the default filter).
const todayStr = () => new Date().toLocaleDateString('en-CA');

const MJ_LIMIT = 20;   // manual jobs per page

const CATEGORIES = ['beginner', 'intermediate', 'advanced', 'college', 'other'];
const CAT_LABEL = { beginner: 'Beginner', intermediate: 'Intermediate', advanced: 'Advanced', college: 'College', other: 'Other' };

const defaultCsForm = () => ({
  course_code: '', icon: '', cover_image: '', title: '', tagline: '',
  target_audience: '', duration: '', overview: '',
  learn_highlights: [], outcome_summary: '', outcomes: [],
  price: '', category: 'beginner', is_active: true, is_enrollable: true,
});

// ── List editor (highlights / outcomes) ──────────────────────────────────────
const ListEditor = ({ items, onChange, placeholder }) => (
  <div className="adm-list-editor">
    {items.map((item, i) => (
      <div key={i} className="adm-list-row">
        <span className="adm-list-bullet">•</span>
        <input value={item} placeholder={placeholder}
          onChange={e => { const n = [...items]; n[i] = e.target.value; onChange(n); }} />
        <button type="button" className="adm-list-rm" title="Remove" onClick={() => onChange(items.filter((_, j) => j !== i))}>✕</button>
      </div>
    ))}
    <button type="button" className="adm-list-add" onClick={() => onChange([...items, ''])}>+ Add item</button>
  </div>
);

// ── Module editor dialog ──────────────────────────────────────────────────────
const ModuleEditor = ({ course, onClose, onSaved, showToast }) => {
  const [modules, setModules] = React.useState(() => JSON.parse(JSON.stringify(course.modules || [])));
  const [saving, setSaving] = React.useState(false);

  const updMod = (i, patch) => setModules(m => m.map((x, j) => j === i ? { ...x, ...patch } : x));
  const removeMod = (i) => setModules(m => m.filter((_, j) => j !== i));
  const addMod = () => setModules(m => [...m, { title: '', sessions: [] }]);

  const addSess = (mi) => setModules(m => m.map((mod, j) => j === mi
    ? { ...mod, sessions: [...mod.sessions, { number: '', title: '', duration: '', topics: [], note: '' }] }
    : mod));
  const removeSess = (mi, si) => setModules(m => m.map((mod, j) => j === mi
    ? { ...mod, sessions: mod.sessions.filter((_, k) => k !== si) }
    : mod));
  const updSess = (mi, si, f, v) => setModules(m => m.map((mod, j) => j === mi
    ? { ...mod, sessions: mod.sessions.map((s, k) => k === si ? { ...s, [f]: v } : s) }
    : mod));

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = getAdminToken();
      await adminUpdateCourse(token, course._id, { modules });
      showToast('Modules saved.', 'success');
      onSaved(course._id, modules);
      onClose();
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not save modules.');
    } finally { setSaving(false); }
  };

  return (
    <div className="adm-overlay" onClick={onClose}>
      <div className="adm-dialog adm-dialog-wide adm-dialog-tall" onClick={e => e.stopPropagation()}>
        <div className="adm-dlg-hd">
          <h3>Modules <span style={{ fontWeight: 400, color: 'var(--text-3)', fontSize: 13 }}>— {course.title}</span></h3>
          <button className="adm-dlg-close" onClick={onClose}>✕</button>
        </div>
        <div className="adm-dlg-body adm-modules-body">
          {modules.length === 0 && (
            <p style={{ color: 'var(--text-3)', textAlign: 'center', padding: '24px 0', fontSize: 13 }}>
              No modules yet. Click "+ Add Module" to get started.
            </p>
          )}
          {modules.map((mod, mi) => (
            <div key={mi} className="adm-module-card">
              <div className="adm-module-hd">
                <span className="adm-module-num">Module {mi + 1}</span>
                <input className="adm-module-title-inp" value={mod.title} placeholder="Module title *"
                  onChange={e => updMod(mi, { title: e.target.value })} />
                <button type="button" className="adm-icon-btn adm-icon-btn-danger" title="Remove module"
                  onClick={() => removeMod(mi)}>
                  <Icon paths="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" size={13} />
                </button>
              </div>
              <div className="adm-sessions">
                {mod.sessions.length === 0 && (
                  <p style={{ color: 'var(--text-3)', fontSize: 12, margin: '2px 0 4px' }}>No sessions yet.</p>
                )}
                {mod.sessions.map((s, si) => (
                  <div key={si} className="adm-session-card">
                    <div className="adm-session-row1">
                      <input className="adm-inp-xs" placeholder="#" value={s.number}
                        onChange={e => updSess(mi, si, 'number', e.target.value)} />
                      <input className="adm-inp-flex" placeholder="Session title *" value={s.title}
                        onChange={e => updSess(mi, si, 'title', e.target.value)} />
                      <input className="adm-inp-sm" placeholder="Duration" value={s.duration}
                        onChange={e => updSess(mi, si, 'duration', e.target.value)} />
                      <button type="button" className="adm-icon-btn adm-icon-btn-danger"
                        onClick={() => removeSess(mi, si)}>
                        <Icon paths="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" size={12} />
                      </button>
                    </div>
                    <textarea className="adm-sess-topics" rows={2}
                      placeholder="Topics — one per line"
                      value={(s.topics || []).join('\n')}
                      onChange={e => updSess(mi, si, 'topics',
                        e.target.value.split('\n').map(t => t.trim()).filter(Boolean))} />
                  </div>
                ))}
                <button type="button" className="adm-btn adm-btn-ghost adm-btn-sm"
                  style={{ marginTop: 4 }} onClick={() => addSess(mi)}>
                  + Add Session
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="adm-dlg-ft">
          <button type="button" className="adm-btn adm-btn-ghost" style={{ marginRight: 'auto' }}
            onClick={addMod}>+ Add Module</button>
          <button type="button" className="adm-btn adm-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="adm-btn adm-btn-accent" disabled={saving} onClick={handleSave}>
            {saving ? 'Saving…' : 'Save Modules'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Inline SVG icon ───────────────────────────────────────────────────────────
const Icon = ({ paths, size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    {(Array.isArray(paths) ? paths : [paths]).map((d, i) => <path key={i} d={d} />)}
  </svg>
);

const IC = {
  dashboard:       ['M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z', 'M9 22V12h6v10'],
  contactmessages: ['M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z', 'M22 6l-10 7L2 6'],
  blogs:           ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M16 13H8', 'M16 17H8', 'M10 9H8'],
  courses:         ['M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z', 'M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z'],
  payments:        ['M21 4H3a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z', 'M1 10h22'],
  users:           ['M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', 'M9 7a4 4 0 1 0 8 0 4 4 0 0 0-8 0', 'M23 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75'],
  profile:         ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'],
  adminUsers:      ['M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', 'M9 7a4 4 0 1 0 8 0 4 4 0 0 0-8 0', 'M23 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75', 'M19 8v6', 'M22 11h-6'],
  logout:          ['M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4', 'M16 17l5-5-5-5', 'M21 12H9'],
  chevronL:        'M15 18l-6-6 6-6',
  chevronR:        'M9 18l6-6-6-6',
  chevronD:        'M6 9l6 6 6-6',
  chevronU:        'M18 15l-6-6-6 6',
  menu:            ['M3 12h18', 'M3 6h18', 'M3 18h18'],
  filter:          'M22 3H2l8 9.46V19l4 2v-8.54z',
  reset:           ['M23 4v6h-6', 'M1 20v-6h6', 'M3.51 9a9 9 0 0 1 14.85-3.36L23 10', 'M1 14l4.64 4.36A9 9 0 0 0 20.49 15'],
  view:            ['M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z', 'M12 12m-3 0a3 3 0 1 0 6 0 3 3 0 0 0-6 0'],
  edit:            ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
  analytics:       ['M18 20V10', 'M12 20V4', 'M6 20v-6'],
  check:           ['M20 6L9 17l-5-5'],
  lock:            ['M19 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2z', 'M7 11V7a5 5 0 0 1 10 0v4'],
  eye:             ['M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z', 'M12 12m-3 0a3 3 0 1 0 6 0 3 3 0 0 0-6 0'],
  eyeOff:          ['M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24', 'M1 1l22 22'],
  // Paper Trading group icons
  papertrading:    ['M22 7 13.5 15.5 8.5 10.5 2 17', 'M16 7h6v6'],
  stocks:          ['M3 3v18h18', 'M7 16l4-4 4 4 4-8'],
  marketdata:      ['M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z', 'M12 8v4l3 3'],
  tradingactivity: ['M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', 'M9 7a4 4 0 1 0 8 0 4 4 0 0 0-8 0', 'M22 11h-6M19 8v6'],
  tools:           ['M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z'],
};

// ── RBAC ─────────────────────────────────────────────────────────────────────
const ALL_ROLES = ['superadmin', 'blogadmin', 'contactmessageadmin'];

const ROLE_LABELS = {
  superadmin: 'Super Admin',
  blogadmin: 'Blog Admin',
  contactmessageadmin: 'Contact Message Admin',
};

const ROLE_PERMISSIONS = {
  superadmin: ['dashboard', 'adminusers', 'users', 'courses', 'tools', 'contactmessages', 'blogs', 'payments', 'stocks', 'marketdata', 'tradingactivity'],
  blogadmin: ['blogs'],
  contactmessageadmin: ['contactmessages'],
};

const getPermissions = (roles = []) => {
  const perms = new Set();
  (Array.isArray(roles) ? roles : [roles]).forEach((r) => {
    (ROLE_PERMISSIONS[r] || []).forEach((p) => perms.add(p));
  });
  return perms;
};

const SECTIONS = [
  { id: 'dashboard',       label: 'Dashboard',        icon: IC.dashboard },
  { id: 'adminusers',      label: 'Admin Users',       icon: IC.adminUsers },
  { id: 'users',           label: 'Normal Users',      icon: IC.users },
  { id: 'courses',         label: 'Courses',           icon: IC.courses },
  { id: 'tools',           label: 'Tools',             icon: IC.tools },
  { id: 'blogs',           label: 'Blogs',             icon: IC.blogs },
  { id: 'payments',        label: 'Payments',          icon: IC.payments },
  { id: 'contactmessages', label: 'Contact Messages',  icon: IC.contactmessages },
  // Paper Trading group — rendered as collapsible in the sidebar
  {
    group:    true,
    id:       'papertrading',
    label:    'Paper Trading',
    icon:     IC.papertrading,
    children: [
      { id: 'stocks',          label: 'Stocks',           icon: IC.stocks },
      { id: 'marketdata',      label: 'Market Data',      icon: IC.marketdata },
      { id: 'tradingactivity', label: 'Trading Activity', icon: IC.tradingactivity },
    ],
  },
];

const CONTACT_STATUSES = ['new', 'acknowledged', 'replied', 'closed'];
const STATUS_COLORS = { new: 'var(--red)', acknowledged: 'var(--amber)', replied: 'var(--accent-2)', closed: 'var(--green)' };

const getTodayRange = () => {
  const pad = (n) => String(n).padStart(2, '0');
  const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const s = new Date(); s.setHours(0, 0, 0, 0);
  const e = new Date(); e.setHours(23, 59, 59, 999);
  return { fromDate: fmt(s), toDate: fmt(e) };
};

const emptyFilters = () => ({ ...getTodayRange(), name: '', email: '', phone: '', subject: '', status: 'all' });

// ── Main Component ────────────────────────────────────────────────────────────
export default function AdminDashboard({ theme, setTheme }) {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Persist the active sub-tab in the URL (?tab=) so a page refresh restores it
  // instead of snapping back to the section's default tab.
  const updateTabParam = (id) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', id);
      return next;
    }, { replace: true });
  };

  // Derive active section from URL path: /admin/<section>
  const activeSection = (() => {
    const seg = location.pathname.split('/').filter(Boolean).pop();
    const valid = ['dashboard', 'contactmessages', 'blogs', 'users', 'adminusers', 'courses', 'tools', 'payments', 'profile', 'stocks', 'marketdata', 'tradingactivity'];
    return valid.includes(seg) ? seg : 'dashboard';
  })();

  // contacts state
  const [contacts, setContacts] = useState([]);
  const [cLoading, setCLoading] = useState(true);
  const [filters, setFilters] = useState(emptyFilters);
  const [filtered, setFiltered] = useState([]);
  const [cPage, setCPage] = useState(0);
  const [viewMsg, setViewMsg] = useState(null);
  const [editMsg, setEditMsg] = useState(null);
  const [editStatus, setEditStatus] = useState('new');

  // users state
  const [users, setUsers] = useState([]);
  const [uLoading, setULoading] = useState(false);
  const [uSearch, setUSearch] = useState('');
  const [uPage, setUPage] = useState(0);
  const [uTotal, setUTotal] = useState(0);
  const [uStatusSaving, setUStatusSaving] = useState(null); // userId being toggled
  const U_LIMIT = 25;

  // dashboard stats
  const [stats, setStats] = useState(null);

  // profile state
  const [adminInfo, setAdminInfo] = useState(getAdminData());
  const [profForm, setProfForm] = useState({ name: '', email: '' });
  const [profSaving, setProfSaving] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [showPw, setShowPw] = useState({ cur: false, nw: false, cnf: false });

  // admin users state
  const [adminUsers, setAdminUsers] = useState([]);
  const [auLoading, setAuLoading] = useState(false);
  const [auShowCreate, setAuShowCreate] = useState(false);
  const [auCreateForm, setAuCreateForm] = useState({ name: '', email: '', password: '', roles: ['superadmin'] });
  const [auCreateSaving, setAuCreateSaving] = useState(false);

  // courses state
  const [courseList, setCourseList] = useState([]);
  const [csLoading, setCsLoading] = useState(false);
  const [csShowForm, setCsShowForm] = useState(false);
  const [csEditId, setCsEditId] = useState(null);
  const [csForm, setCsForm] = useState(defaultCsForm);
  const [csSaving, setCsSaving] = useState(false);
  const [csCoverUploading, setCsCoverUploading] = useState(false);
  const handleCsCoverChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      setCsCoverUploading(true);
      const res = await adminUploadCourseImage(getAdminToken(), file);
      const url = res.data?.data?.url || '';
      if (url) setCsForm((p) => ({ ...p, cover_image: url }));
    } catch (err) {
      showToast(err?.response?.data?.message || 'Cover upload failed', 'error');
    } finally {
      setCsCoverUploading(false);
    }
  };
  const [csDeleteTarget, setCsDeleteTarget] = useState(null);
  const [csModulesTarget, setCsModulesTarget] = useState(null);
  const [csViewTarget, setCsViewTarget] = useState(null);

  // ── Tools state ────────────────────────────────────────────────────────────
  const [toolList,       setToolList]       = useState([]);
  const [tlLoading,      setTlLoading]      = useState(false);
  const [tlShowForm,     setTlShowForm]      = useState(false);
  const [tlEditTarget,   setTlEditTarget]   = useState(null);
  const [tlForm,         setTlForm]         = useState({});
  const [tlSaving,       setTlSaving]       = useState(false);
  const [tlDeleteTarget, setTlDeleteTarget] = useState(null);
  const [tlViewTarget,   setTlViewTarget]   = useState(null);

  // ── Stocks state ──────────────────────────────────────────────────────────
  const [stockList,  setStockList]  = useState([]);
  const [stLoading,  setStLoading]  = useState(false);
  const [stSearch,   setStSearch]   = useState('');
  const [stPage,     setStPage]     = useState(0);
  const [stTotal,    setStTotal]    = useState(0);
  const [stShowForm, setStShowForm] = useState(false);
  const [stEditTarget, setStEditTarget] = useState(null); // null = add, object = edit
  const [stForm,     setStForm]     = useState({ symbol: '', company_name: '', sector: '', exchange: 'NSE', market_cap_category: '' });
  const [stSaving,   setStSaving]   = useState(false);
  const [stToggling, setStToggling] = useState(null); // id being toggled
  const [stDeleteTarget, setStDeleteTarget] = useState(null);
  const [stDeleting,     setStDeleting]     = useState(false);
  const [stYahooResults, setStYahooResults] = useState([]);
  const [stYahooSearching, setStYahooSearching] = useState(false);
  const ST_LIMIT = 50;

  // ── Market Data state ─────────────────────────────────────────────────────
  const [mdTab,         setMdTab]         = useState(
    activeSection === 'marketdata' && ['monitor', 'jobs', 'provider'].includes(searchParams.get('tab'))
      ? searchParams.get('tab') : 'monitor'
  );  // 'monitor' | 'jobs' | 'provider'
  const [mdQuotes,      setMdQuotes]      = useState([]);       // latest per symbol
  const [mdHistory,     setMdHistory]     = useState([]);       // history rows
  const [mdSelSym,      setMdSelSym]      = useState(null);     // selected symbol (history modal)
  const [mdLoading,     setMdLoading]     = useState(false);
  const [mdHistLoading, setMdHistLoading] = useState(false);    // history modal loading
  const [mdHistPage,    setMdHistPage]    = useState(0);        // history modal page

  // ── Manual jobs state ──────────────────────────────────────────────────────
  const [mjJobs,       setMjJobs]       = useState([]);         // jobs in the current filter window
  const [mjLoading,    setMjLoading]    = useState(false);
  const [mjSelId,      setMjSelId]      = useState(null);       // selected job id (detail modal)
  const [mjFilter,     setMjFilter]     = useState({ from: todayStr(), to: todayStr(), job_type: '' });
  const [mjPage,       setMjPage]       = useState(0);
  const [mjTotal,      setMjTotal]      = useState(0);
  const [mjTriggerOpen, setMjTriggerOpen] = useState(false);    // trigger-job modal open
  const [mjJobType,    setMjJobType]    = useState('FETCH_QUOTES'); // selected type in trigger modal
  const [mjTriggering, setMjTriggering] = useState(false);      // trigger in flight
  const [bfForm,       setBfForm]       = useState({ scope: 'SYMBOLS', symbols: '', interval: '1d', start_date: '', end_date: '' });

  // ── Trading Activity state ────────────────────────────────────────────────
  const [taTab,       setTaTab]      = useState(
    activeSection === 'tradingactivity' && ['accounts', 'orders'].includes(searchParams.get('tab'))
      ? searchParams.get('tab') : 'accounts'
  );
  const [taAccounts,  setTaAccounts] = useState([]);
  const [taOrders,    setTaOrders]   = useState([]);
  const [taLoading,   setTaLoading]  = useState(false);
  const [taPage,      setTaPage]     = useState(0);
  const [taTotal,     setTaTotal]    = useState(0);
  const [taSearch,    setTaSearch]   = useState('');
  const TA_LIMIT = 25;
  // Trading-activity management (detail view, grant, top-up, delete)
  const [taViewId,       setTaViewId]       = useState(null);
  const [taViewData,     setTaViewData]     = useState(null);
  const [taViewLoading,  setTaViewLoading]  = useState(false);
  const [taShowGrant,    setTaShowGrant]    = useState(false);
  const [taGrantForm,    setTaGrantForm]    = useState({ email: '', cash: '1000000' });
  const [taTopupTarget,  setTaTopupTarget]  = useState(null);
  const [taTopupForm,    setTaTopupForm]    = useState({ amount: '', note: '' });
  const [taDeleteTarget, setTaDeleteTarget] = useState(null);
  const [taActionLoading, setTaActionLoading] = useState(false);

  // ── Sidebar group expand ──────────────────────────────────────────────────
  const [expandedGroups, setExpandedGroups] = useState({ papertrading: true });
  const toggleGroup = (id) => setExpandedGroups(prev => ({ ...prev, [id]: !prev[id] }));

  // global toast
  const [toast, setToast] = useState({ message: '', type: 'error' });
  const showToast = useCallback((message, type = 'error') => setToast({ message, type }), []);

  const userMenuRef = useRef(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // ── Auth guard ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const token = getAdminToken();
    if (!token) { navigate('/admin/signin', { replace: true }); return; }
    fetchContacts();
    fetchAdminProfile();
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!userMenuOpen) return;
    const h = (e) => { if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [userMenuOpen]);

  useEffect(() => { applyFilters(filters); }, [contacts]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Contacts ────────────────────────────────────────────────────────────────
  const fetchContacts = async () => {
    try {
      setCLoading(true);
      const token = getAdminToken();
      if (!token) { navigate('/admin/signin?expired=1', { replace: true }); return; }
      const res = await axios.get(`${apiBaseUrl}/admin/contact/messagelist`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) setContacts(res.data.data || []);
    } catch (err) {
      if (err.response?.status === 401) { clearAdminSession(); navigate('/admin/signin?expired=1', { replace: true }); }
      else showToast(err.response?.data?.message || 'Failed to load contacts');
    } finally { setCLoading(false); }
  };

  const applyFilters = useCallback((f = filters) => {
    let list = [...contacts];
    if (f.name) list = list.filter(c => c.name?.toLowerCase().includes(f.name.toLowerCase()));
    if (f.email) list = list.filter(c => c.email?.toLowerCase().includes(f.email.toLowerCase()));
    if (f.phone) list = list.filter(c => c.phone?.toLowerCase().includes(f.phone.toLowerCase()));
    if (f.subject) list = list.filter(c => c.subject?.toLowerCase().includes(f.subject.toLowerCase()));
    if (f.status !== 'all') list = list.filter(c => (c.status || 'new').toLowerCase() === f.status);
    if (f.fromDate) list = list.filter(c => new Date(c.created_at) >= new Date(f.fromDate));
    if (f.toDate) list = list.filter(c => new Date(c.created_at) <= new Date(f.toDate));
    setFiltered(list); setCPage(0);
  }, [contacts, filters]);

  const handleStatusUpdate = async () => {
    if (!editMsg) return;
    try {
      const token = getAdminToken();
      await axios.patch(`${apiBaseUrl}/admin/contact/messagestatus/${editMsg._id}`, { status: editStatus }, { headers: { Authorization: `Bearer ${token}` } });
      setContacts(prev => prev.map(c => c._id === editMsg._id ? { ...c, status: editStatus } : c));
      setEditMsg(null);
    } catch (_err) { showToast('Failed to update status'); }
  };

  // ── Users ──────────────────────────────────────────────────────────────────
  const fetchUsers = useCallback(async (page = 0, q = uSearch) => {
    try {
      setULoading(true);
      const token = getAdminToken();
      if (!token) { navigate('/admin/signin?expired=1', { replace: true }); return; }
      const res = await axios.get(`${apiBaseUrl}/admin/normaluser/list`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: page + 1, limit: U_LIMIT, q: q || undefined }
      });
      if (res.data.success) { setUsers(res.data.data || []); setUTotal(res.data.pagination?.total || 0); setUPage(page); }
    } catch (err) {
      if (err.response?.status === 401) { clearAdminSession(); navigate('/admin/signin?expired=1', { replace: true }); }
      else showToast(err.response?.data?.message || 'Failed to load users');
    } finally { setULoading(false); }
  }, [navigate, uSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeSection === 'users' && users.length === 0 && !uLoading) fetchUsers(0);
  }, [activeSection]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleUserStatus = async (user) => {
    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    setUStatusSaving(user._id);
    try {
      const token = getAdminToken();
      await axios.patch(`${apiBaseUrl}/admin/normaluser/${user._id}/status`,
        { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(prev => prev.map(u => u._id === user._id ? { ...u, status: newStatus } : u));
      showToast(`User ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully.`, 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not update user status.');
    } finally { setUStatusSaving(null); }
  };

  // ── Stats ──────────────────────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    try {
      const token = getAdminToken();
      if (!token) return;
      const res = await axios.get(`${apiBaseUrl}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) setStats(res.data.data);
    } catch (_) { /* stats are non-critical, fail silently */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeSection === 'dashboard') fetchStats();
  }, [activeSection]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Admin Users ────────────────────────────────────────────────────────────
  const fetchAdminUsers = useCallback(async () => {
    try {
      setAuLoading(true);
      const token = getAdminToken();
      if (!token) { navigate('/admin/signin?expired=1', { replace: true }); return; }
      const res = await axios.get(`${apiBaseUrl}/admin/adminusers/list`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) setAdminUsers(res.data.data || []);
    } catch (err) {
      if (err.response?.status === 401) { clearAdminSession(); navigate('/admin/signin?expired=1', { replace: true }); }
      else showToast(err.response?.data?.message || 'Failed to load admin users');
    } finally { setAuLoading(false); }
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeSection === 'adminusers' && adminUsers.length === 0 && !auLoading) fetchAdminUsers();
  }, [activeSection]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Courses ────────────────────────────────────────────────────────────────
  const fetchCourses = useCallback(async () => {
    try {
      setCsLoading(true);
      const token = getAdminToken();
      const res = await adminListCourses(token);
      if (res.data.success) setCourseList(res.data.data || []);
    } catch (err) {
      if (err.response?.status === 401) { clearAdminSession(); navigate('/admin/signin?expired=1', { replace: true }); }
      else showToast(err.response?.data?.message || 'Failed to load courses');
    } finally { setCsLoading(false); }
  }, [navigate, showToast]);

  useEffect(() => {
    if (activeSection === 'courses' && courseList.length === 0 && !csLoading) fetchCourses();
  }, [activeSection]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCsCreate = () => { setCsEditId(null); setCsForm(defaultCsForm()); setCsShowForm(true); };
  const openCsEdit = (course) => {
    setCsEditId(course._id);
    setCsForm({
      course_id: course.course_id || '',
      course_code: course.course_code || '',
      icon: course.icon || '',
      cover_image: course.cover_image || '',
      title: course.title || '',
      tagline: course.tagline || '',
      target_audience: course.target_audience || '',
      duration: course.duration || '',
      overview: course.overview || '',
      learn_highlights: course.learn_highlights || [],
      outcome_summary: course.outcome_summary || '',
      outcomes: course.outcomes || [],
      price: String(course.price ?? ''),
      category: course.category || 'beginner',
      is_active: course.is_active ?? true,
      is_enrollable: course.is_enrollable ?? true,
    });
    setCsShowForm(true);
  };

  const handleCsSave = async (e) => {
    e.preventDefault();
    const payload = {
      course_code: csForm.course_code.trim(),
      icon: csForm.icon.trim(),
      cover_image: (csForm.cover_image || '').trim(),
      title: csForm.title.trim(),
      tagline: csForm.tagline.trim(),
      target_audience: csForm.target_audience.trim(),
      duration: csForm.duration.trim(),
      overview: csForm.overview.trim(),
      learn_highlights: csForm.learn_highlights.filter(Boolean),
      outcome_summary: csForm.outcome_summary.trim(),
      outcomes: csForm.outcomes.filter(Boolean),
      price: parseFloat(csForm.price) || 0,
      category: csForm.category,
      is_active: csForm.is_active,
      is_enrollable: csForm.is_enrollable,
    };
    setCsSaving(true);
    try {
      const token = getAdminToken();
      if (csEditId) {
        await adminUpdateCourse(token, csEditId, payload);
        showToast('Course updated.', 'success');
      } else {
        await adminCreateCourse(token, payload);
        showToast('Course created.', 'success');
      }
      setCsShowForm(false);
      fetchCourses();
    } catch (err) {
      const msg = err.response?.data?.message
        || (err.response?.data?.errors?.length ? err.response.data.errors.map(e => e.msg).join(' · ') : null)
        || 'Could not save course.';
      showToast(msg);
    } finally { setCsSaving(false); }
  };

  const handleCsDelete = async () => {
    if (!csDeleteTarget) return;
    try {
      const token = getAdminToken();
      await adminDeleteCourse(token, csDeleteTarget._id);
      showToast('Course deleted.', 'success');
      setCourseList(prev => prev.filter(c => c._id !== csDeleteTarget._id));
      setCsDeleteTarget(null);
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not delete course.');
    }
  };

  // ── Tools ──────────────────────────────────────────────────────────────────
  const fetchTools = useCallback(async () => {
    try {
      setTlLoading(true);
      const token = getAdminToken();
      const res = await adminListTools(token);
      if (res.data.success) setToolList(res.data.data || []);
    } catch (err) {
      if (err.response?.status === 401) { clearAdminSession(); navigate('/admin/signin?expired=1', { replace: true }); }
      else showToast(err.response?.data?.message || 'Failed to load tools');
    } finally { setTlLoading(false); }
  }, [navigate, showToast]);

  useEffect(() => {
    if (activeSection === 'tools' && toolList.length === 0 && !tlLoading) fetchTools();
  }, [activeSection]); // eslint-disable-line react-hooks/exhaustive-deps

  const openTlEdit = (tool) => {
    setTlEditTarget(tool);
    setTlForm({
      name: tool.name || '',
      tagline: tool.tagline || '',
      description: tool.description || '',
      icon: tool.icon || '',
      price: String(tool.price ?? '0'),
      is_paid: tool.is_paid ?? false,
      is_active: tool.is_active ?? true,
      is_coming_soon: tool.is_coming_soon ?? false,
      features: tool.features || [],
    });
    setTlShowForm(true);
  };

  const handleToolSave = async (e) => {
    e.preventDefault();
    if (!tlEditTarget) return;
    setTlSaving(true);
    try {
      const token = getAdminToken();
      const payload = {
        name: tlForm.name.trim(),
        tagline: tlForm.tagline.trim(),
        description: tlForm.description.trim(),
        icon: tlForm.icon.trim(),
        price: parseFloat(tlForm.price) || 0,
        is_paid: tlForm.is_paid,
        is_active: tlForm.is_active,
        is_coming_soon: tlForm.is_coming_soon,
        features: tlForm.features.filter(Boolean),
      };
      const res = await adminUpdateTool(token, tlEditTarget._id, payload);
      if (res.data.success) {
        setToolList(prev => prev.map(t => t._id === tlEditTarget._id ? res.data.data : t));
        showToast('Tool updated.', 'success');
        setTlShowForm(false);
        setTlEditTarget(null);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not save tool.');
    } finally { setTlSaving(false); }
  };

  const handleToolDelete = async () => {
    if (!tlDeleteTarget) return;
    try {
      const token = getAdminToken();
      await adminDeleteTool(token, tlDeleteTarget._id);
      showToast('Tool deleted.', 'success');
      setToolList(prev => prev.filter(t => t._id !== tlDeleteTarget._id));
      setTlDeleteTarget(null);
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not delete tool.');
    }
  };

  const handleCreateAdminUser = async (e) => {
    e.preventDefault();
    setAuCreateSaving(true);
    try {
      const token = getAdminToken();
      const res = await axios.post(`${apiBaseUrl}/admin/adminusers/create`, auCreateForm, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) {
        showToast('Admin user created successfully.', 'success');
        setAuCreateForm({ name: '', email: '', password: '', roles: ['superadmin'] });
        setAuShowCreate(false);
        fetchAdminUsers();
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not create admin user.');
    } finally { setAuCreateSaving(false); }
  };

  // ── Admin profile ──────────────────────────────────────────────────────────
  const fetchAdminProfile = async () => {
    try {
      const token = getAdminToken();
      if (!token) return;
      const res = await axios.get(`${apiBaseUrl}/admin/user/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) {
        const d = res.data.data;
        setAdminInfo(d);
        setProfForm({ name: d.name || '', email: d.email || '' });
      }
    } catch (_err) { /* non-critical */ }
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfSaving(true);
    try {
      const token = getAdminToken();
      const res = await axios.put(`${apiBaseUrl}/admin/user/me`,
        { name: profForm.name, email: profForm.email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setAdminInfo(res.data.data);
        setAdminSession(token, res.data.data);
        showToast('Profile updated successfully.', 'success');
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not save changes.');
    } finally { setProfSaving(false); }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm_password) {
      showToast('New passwords do not match.'); return;
    }
    setPwSaving(true);
    try {
      const token = getAdminToken();
      await axios.put(`${apiBaseUrl}/admin/user/password`,
        { current_password: pwForm.current_password, new_password: pwForm.new_password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast('Password changed successfully.', 'success');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not change password.');
    } finally { setPwSaving(false); }
  };

  // ── RBAC ───────────────────────────────────────────────────────────────────
  const myPermissions = useMemo(
    () => getPermissions(adminInfo?.roles || []),
    [adminInfo]
  );
  const visibleSections = useMemo(() => {
    return SECTIONS
      .map(s => {
        if (s.group) {
          const visChildren = s.children.filter(c => myPermissions.has(c.id));
          return visChildren.length ? { ...s, children: visChildren } : null;
        }
        return myPermissions.has(s.id) ? s : null;
      })
      .filter(Boolean);
  }, [myPermissions]);

  // ── Stocks ─────────────────────────────────────────────────────────────────
  const fetchStocks = useCallback(async (page = 0, q = stSearch) => {
    try {
      setStLoading(true);
      const token = getAdminToken();
      const res = await axios.get(`${apiBaseUrl}/admin/stocks`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: page + 1, limit: ST_LIMIT, q: q || undefined },
      });
      if (res.data.success) {
        setStockList(res.data.data?.stocks || []);
        setStTotal(res.data.data?.pagination?.total || 0);
        setStPage(page);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load stocks');
    } finally { setStLoading(false); }
  }, [stSearch, showToast]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeSection === 'stocks' && stockList.length === 0 && !stLoading) fetchStocks(0);
  }, [activeSection]); // eslint-disable-line react-hooks/exhaustive-deps

  const stYahooTimer = useRef(null);

  const openStockAdd = () => {
    setStEditTarget(null);
    setStForm({ symbol: '', company_name: '', sector: '', exchange: 'NSE', market_cap_category: '' });
    setStYahooResults([]);
    setStShowForm(true);
  };

  const searchYahooStocks = (q) => {
    clearTimeout(stYahooTimer.current);
    if (!q.trim()) { setStYahooResults([]); setStYahooSearching(false); return; }
    setStYahooSearching(true);
    stYahooTimer.current = setTimeout(async () => {
      try {
        const token = getAdminToken();
        const res = await axios.get(`${apiBaseUrl}/admin/stocks/search`, {
          headers: { Authorization: `Bearer ${token}` }, params: { q: q.trim() },
        });
        setStYahooResults(res.data?.success ? (res.data.data?.results || []) : []);
      } catch { setStYahooResults([]); }
      finally { setStYahooSearching(false); }
    }, 350);
  };

  const applyYahooResult = (r) => {
    setStForm(f => ({
      ...f,
      symbol: r.symbol,
      company_name: r.company_name,
      exchange: r.exchange || 'NSE',
      sector: r.sector || f.sector,
    }));
    setStYahooResults([]);
  };

  const handleStockDelete = async () => {
    if (!stDeleteTarget) return;
    setStDeleting(true);
    try {
      const token = getAdminToken();
      const res = await axios.delete(`${apiBaseUrl}/admin/stocks/${stDeleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showToast(res.data.data?.message || 'Stock deleted.', 'success');
      setStDeleteTarget(null);
      fetchStocks(stPage);
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not delete stock.');
    } finally { setStDeleting(false); }
  };
  const openStockEdit = (stock) => {
    setStEditTarget(stock);
    setStForm({ symbol: stock.symbol, company_name: stock.company_name, sector: stock.sector || '', exchange: stock.exchange || 'NSE', market_cap_category: stock.market_cap_category || '' });
    setStShowForm(true);
  };

  const handleStockSave = async (e) => {
    e.preventDefault();
    setStSaving(true);
    try {
      const token = getAdminToken();
      if (stEditTarget) {
        await axios.put(`${apiBaseUrl}/admin/stocks/${stEditTarget.id}`, stForm, { headers: { Authorization: `Bearer ${token}` } });
        showToast('Stock updated.', 'success');
      } else {
        await axios.post(`${apiBaseUrl}/admin/stocks`, stForm, { headers: { Authorization: `Bearer ${token}` } });
        showToast('Stock added.', 'success');
      }
      setStShowForm(false);
      fetchStocks(stPage);
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not save stock.');
    } finally { setStSaving(false); }
  };

  const handleStockToggle = async (stock) => {
    setStToggling(stock.id);
    try {
      const token = getAdminToken();
      const updatedForm = { ...stock, is_active: !stock.is_active };
      const res = await axios.put(`${apiBaseUrl}/admin/stocks/${stock.id}`, updatedForm, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) {
        setStockList(prev => prev.map(s => s.id === stock.id ? { ...s, is_active: !s.is_active } : s));
        showToast(res.data.data?.message, 'success');
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not update status.');
    } finally { setStToggling(null); }
  };

  // ── Market Data ─────────────────────────────────────────────────────────────
  const fetchMdLiveData = useCallback(async () => {
    setMdLoading(true);
    try {
      const tok = getAdminToken();
      const res = await axios.get(`${apiBaseUrl}/admin/marketdata/livedata`, { headers: { Authorization: `Bearer ${tok}` } });
      if (res.data.success) setMdQuotes(res.data.data.quotes || []);
    } catch { /* silent */ }
    finally { setMdLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMdHistory = useCallback(async (sym) => {
    setMdSelSym(sym);
    setMdHistory([]);
    setMdHistPage(0);
    setMdHistLoading(true);
    try {
      const tok = getAdminToken();
      const res = await axios.get(`${apiBaseUrl}/admin/marketdata/livedata`, {
        headers: { Authorization: `Bearer ${tok}` },
        params: { symbol: sym, limit: 100 },
      });
      if (res.data.success) setMdHistory(res.data.data.history || []);
    } catch { /* silent */ }
    finally { setMdHistLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const closeMdHistory = () => { setMdSelSym(null); setMdHistory([]); };

  useEffect(() => {
    if (activeSection === 'marketdata' && mdTab === 'monitor') fetchMdLiveData();
  }, [activeSection, mdTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Manual jobs: explicit fetch (forward icon) with pagination ──────────────
  // Fetching is on-demand only — the forward icon (or the pager) calls this; an
  // optional `override` filter lets callers fetch with a not-yet-committed filter.
  const fetchManualJobs = useCallback(async (page = 0, override) => {
    const flt = override || mjFilter;
    setMjLoading(true);
    try {
      const tok = getAdminToken();
      const res = await listManualJobs(tok, {
        page, limit: MJ_LIMIT,
        from: flt.from || undefined,
        to: flt.to || undefined,
        job_type: flt.job_type || undefined,
      });
      if (res.data.success) {
        setMjJobs(res.data.data.jobs || []);
        setMjTotal(res.data.data.total || 0);
        setMjPage(res.data.data.page ?? page);
      }
    } catch { /* silent */ }
    finally { setMjLoading(false); }
  }, [mjFilter]);

  const triggerJob = async () => {
    setMjTriggering(true);
    try {
      const tok = getAdminToken();
      if (mjJobType === 'BACKFILL_HISTORICAL_QUOTES') {
        const { scope, symbols, interval, start_date, end_date } = bfForm;
        if (!start_date || !end_date) { showToast('Start and end date are required.'); setMjTriggering(false); return; }
        if (start_date > end_date) { showToast('Start date must be on or before end date.'); setMjTriggering(false); return; }
        if (scope !== 'ALL' && !symbols.trim()) { showToast('Enter at least one symbol, or choose "All active stocks".'); setMjTriggering(false); return; }
        const payload = {
          scope, interval, start_date, end_date,
          ...(scope === 'ALL' ? {} : { symbols: symbols.split(',').map(s => s.trim().toUpperCase()).filter(Boolean) }),
        };
        const res = await triggerBackfillJob(tok, payload);
        if (res.data.success) showToast('Backfill job started.', 'success');
      } else {
        const taskMap = { FETCH_QUOTES: 'fetch_quotes_data', FETCH_OHLCV: 'fetch_ohlcv_eod' };
        const res = await triggerSimpleTask(tok, taskMap[mjJobType]);
        if (res.data.success) showToast(res.data.data?.message || 'Job started.', 'success');
      }
      setMjTriggerOpen(false);
      setBfForm(f => ({ ...f, symbols: '' }));
      // Ensure a just-triggered job is visible even if the filter excludes today.
      const flt = { ...mjFilter, to: todayStr() };
      setMjFilter(flt);
      fetchManualJobs(0, flt);
    } catch (err) {
      showToast(err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Failed to start job.');
    } finally { setMjTriggering(false); }
  };

  // Refresh icon = revert filters to defaults (today / today / all types). It does
  // not fetch — the admin then clicks the forward icon to pull the data.
  const resetManualJobsFilters = () => { setMjFilter({ from: todayStr(), to: todayStr(), job_type: '' }); setMjPage(0); };

  // Initial load when the Manual Jobs tab opens. Thereafter fetching is explicit
  // (forward icon / pager) — no background polling.
  useEffect(() => {
    if (activeSection === 'marketdata' && mdTab === 'jobs') fetchManualJobs(0);
  }, [activeSection, mdTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Trading Activity ────────────────────────────────────────────────────────
  const fetchTradingAccounts = useCallback(async (page = 0, q = taSearch) => {
    try {
      setTaLoading(true);
      const token = getAdminToken();
      const res = await axios.get(`${apiBaseUrl}/admin/tradingactivity/accounts`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: page + 1, limit: TA_LIMIT, q: q || undefined },
      });
      if (res.data.success) {
        setTaAccounts(res.data.data?.accounts || []);
        setTaTotal(res.data.data?.pagination?.total || 0);
        setTaPage(page);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load accounts.');
    } finally { setTaLoading(false); }
  }, [taSearch, showToast]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTradingOrders = useCallback(async (page = 0) => {
    try {
      setTaLoading(true);
      const token = getAdminToken();
      const res = await axios.get(`${apiBaseUrl}/admin/tradingactivity/orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: page + 1, limit: TA_LIMIT },
      });
      if (res.data.success) {
        setTaOrders(res.data.data?.orders || []);
        setTaTotal(res.data.data?.pagination?.total || 0);
        setTaPage(page);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load orders.');
    } finally { setTaLoading(false); }
  }, [showToast]);

  const openTaDetail = useCallback(async (id) => {
    setTaViewId(id); setTaViewData(null); setTaViewLoading(true);
    try {
      const token = getAdminToken();
      const res = await axios.get(`${apiBaseUrl}/admin/tradingactivity/accounts/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) setTaViewData(res.data.data);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load account detail.');
    } finally { setTaViewLoading(false); }
  }, [showToast]);

  const closeTaDetail = () => { setTaViewId(null); setTaViewData(null); };

  const submitGrant = async () => {
    const email = taGrantForm.email.trim();
    if (!email) { showToast('Enter the user email.'); return; }
    setTaActionLoading(true);
    try {
      const token = getAdminToken();
      const res = await axios.post(`${apiBaseUrl}/admin/tradingactivity/grant`,
        { email, cash: parseFloat(taGrantForm.cash) || 1000000 },
        { headers: { Authorization: `Bearer ${token}` } });
      showToast(res.data.data?.message || 'Tool granted.', 'success');
      setTaShowGrant(false); setTaGrantForm({ email: '', cash: '1000000' });
      fetchTradingAccounts(0);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to grant tool.');
    } finally { setTaActionLoading(false); }
  };

  const submitTopup = async () => {
    const acctId = taTopupTarget?.id;
    const amount = parseFloat(taTopupForm.amount);
    if (!acctId) return;
    if (isNaN(amount) || amount === 0) { showToast('Enter a non-zero amount.'); return; }
    setTaActionLoading(true);
    try {
      const token = getAdminToken();
      const res = await axios.post(`${apiBaseUrl}/admin/tradingactivity/accounts/${acctId}/topup`,
        { amount, note: taTopupForm.note },
        { headers: { Authorization: `Bearer ${token}` } });
      showToast(res.data.data?.message || 'Cash updated.', 'success');
      setTaTopupTarget(null); setTaTopupForm({ amount: '', note: '' });
      fetchTradingAccounts(taPage);
      if (taViewId === acctId) openTaDetail(acctId);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to update cash.');
    } finally { setTaActionLoading(false); }
  };

  const submitDelete = async () => {
    const acctId = taDeleteTarget?.id;
    if (!acctId) return;
    setTaActionLoading(true);
    try {
      const token = getAdminToken();
      const res = await axios.delete(`${apiBaseUrl}/admin/tradingactivity/accounts/${acctId}`,
        { headers: { Authorization: `Bearer ${token}` } });
      showToast(res.data.data?.message || 'Account deleted.', 'success');
      const wasViewing = taViewId === acctId;
      setTaDeleteTarget(null);
      if (wasViewing) closeTaDetail();
      fetchTradingAccounts(0);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete account.');
    } finally { setTaActionLoading(false); }
  };

  const submitSquareOff = async () => {
    setTaActionLoading(true);
    try {
      const token = getAdminToken();
      const res = await axios.post(`${apiBaseUrl}/admin/tradingactivity/squareoff`, {}, { headers: { Authorization: `Bearer ${token}` } });
      showToast(res.data.data?.message || 'Square-off complete.', 'success');
      fetchTradingAccounts(taPage);
    } catch (err) {
      showToast(err.response?.data?.message || 'Square-off failed.');
    } finally { setTaActionLoading(false); }
  };

  useEffect(() => {
    if (activeSection === 'tradingactivity') {
      if (taTab === 'accounts') fetchTradingAccounts(0);
      else fetchTradingOrders(0);
    }
  }, [activeSection, taTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Redirect to first permitted section if current section is not allowed
  useEffect(() => {
    if (!myPermissions.size || activeSection === 'profile') return;
    if (!myPermissions.has(activeSection)) {
      const first = visibleSections[0];
      if (first) navigate(`/admin/${first.id}`, { replace: true });
    }
  }, [activeSection, myPermissions, visibleSections, navigate]);

  // ── Analytics ──────────────────────────────────────────────────────────────
  const analytics = useMemo(() => {
    const total = contacts.length;
    const byStatus = CONTACT_STATUSES.reduce((acc, s) => {
      acc[s] = contacts.filter(c => (c.status || 'new').toLowerCase() === s).length;
      return acc;
    }, {});
    const today = contacts.filter(c => c.created_at && new Date(c.created_at).toDateString() === new Date().toDateString()).length;
    return { total, today, byStatus };
  }, [contacts]);

  const C_PER_PAGE = 25;
  const cTotalPages = Math.ceil(filtered.length / C_PER_PAGE);
  const cPaginated = filtered.slice(cPage * C_PER_PAGE, (cPage + 1) * C_PER_PAGE);

  const uTotalPages = Math.ceil(uTotal / U_LIMIT);

  const logout = () => { clearAdminSession(); navigate('/admin/signin', { replace: true }); };

  const initials = (name) => (name || '?').charAt(0).toUpperCase();

  // ── Navigate section ───────────────────────────────────────────────────────
  const goSection = (id) => {
    navigate(`/admin/${id}`);
    setUserMenuOpen(false);
  };

  // ── Renders ────────────────────────────────────────────────────────────────

  const renderDashboard = () => (
    <div className="adm-section">
      <div className="adm-section-hd">
        <h2>Platform Overview</h2>
        <p>High-level snapshot of platform activity, users, and content.</p>
      </div>

      {/* Primary stats */}
      <div className="adm-stat-grid">
        {[
          { label: 'Normal Users', value: stats?.normalUsers ?? '—', sub: `${stats?.activeUsers ?? '—'} active`, color: 'var(--accent)', icon: '👤' },
          { label: 'Admin Users', value: stats?.adminUsers ?? '—', sub: 'Platform admins', color: 'var(--accent-2)', icon: '🛡️' },
          { label: 'Courses', value: stats?.courses ?? '—', sub: 'In catalogue', color: 'var(--green)', icon: '📚' },
          { label: 'Blog Posts', value: stats?.blogs ?? '—', sub: 'Published & drafts', color: 'var(--amber)', icon: '📝' },
          { label: 'Contact Messages', value: stats?.contacts ?? '—', sub: `${analytics.byStatus?.new ?? 0} open`, color: 'var(--red)', icon: '✉️' },
        ].map(s => (
          <div key={s.label} className="adm-stat-card adm-stat-card-v2">
            <div className="adm-stat-icon-row">
              <span className="adm-stat-icon-badge" style={{ background: `color-mix(in srgb, ${s.color} 14%, transparent)`, color: s.color }}>{s.icon}</span>
              <div className="adm-stat-bullet" style={{ background: s.color }} />
            </div>
            <div className="adm-stat-val">{s.value}</div>
            <div className="adm-stat-lbl">{s.label}</div>
            <div className="adm-stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      <div className="adm-dash-grid">
        <div className="adm-card">
          <h3 className="adm-card-title">Contact Lead Pipeline</h3>
          {CONTACT_STATUSES.map(s => {
            const count = analytics.byStatus[s] || 0;
            const pct = analytics.total ? Math.round((count / analytics.total) * 100) : 0;
            return (
              <div key={s} className="adm-pipe-row">
                <div className="adm-pipe-meta">
                  <span style={{ color: STATUS_COLORS[s], textTransform: 'capitalize', fontWeight: 600 }}>{s}</span>
                  <span className="adm-pipe-count">{count} · {pct}%</span>
                </div>
                <div className="adm-pipe-track"><div className="adm-pipe-fill" style={{ width: `${pct}%`, background: STATUS_COLORS[s] }} /></div>
              </div>
            );
          })}
        </div>
        <div className="adm-card">
          <h3 className="adm-card-title">Coming next</h3>
          <ul className="adm-roadmap">
            {['Payment reconciliation', 'User subscription management', 'Content performance analytics', 'Blog performance metrics'].map(i => (
              <li key={i}><span className="adm-road-dot" />{i}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );

  const renderMessages = () => (
    <div className="adm-section">
      <div className="adm-section-hd">
        <h2>Contact Messages</h2>
        <p>Filter, review and update follow-up status for all inbound enquiries.</p>
      </div>

      <div className="adm-card adm-filter-card">
        <div className="adm-filter-grid">
          {[['From', 'fromDate', 'datetime-local'], ['To', 'toDate', 'datetime-local'], ['Name', 'name', 'text'], ['Email', 'email', 'text'], ['Phone', 'phone', 'text'], ['Subject', 'subject', 'text']].map(([lbl, field, type]) => (
            <label key={field} className="adm-field">
              <span>{lbl}</span>
              <input type={type} value={filters[field]} onChange={e => setFilters(p => ({ ...p, [field]: e.target.value }))} placeholder={type === 'text' ? `Filter by ${lbl.toLowerCase()}…` : undefined} />
            </label>
          ))}
          <label className="adm-field">
            <span>Status</span>
            <select value={filters.status} onChange={e => setFilters(p => ({ ...p, status: e.target.value }))}>
              <option value="all">All statuses</option>
              {CONTACT_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
          </label>
          <div className="adm-filter-btns">
            <button className="adm-btn adm-btn-accent" onClick={() => applyFilters()}><Icon paths={IC.filter} size={14} /> Apply</button>
            <button className="adm-btn adm-btn-ghost" onClick={() => { const f = emptyFilters(); setFilters(f); applyFilters(f); }}><Icon paths={IC.reset} size={14} /> Reset</button>
          </div>
        </div>
      </div>

      <div className="adm-table-bar">
        <span className="adm-pill">{filtered.length} record{filtered.length !== 1 ? 's' : ''}</span>
        <button className="adm-btn adm-btn-ghost adm-btn-xs" onClick={fetchContacts}>Refresh</button>
      </div>


      {cLoading
        ? <div className="adm-spin-wrap"><div className="adm-spinner" /></div>
        : <div className="adm-table-wrap">
          <table className="adm-table">
            <thead><tr>
              <th>Name</th><th>Email</th><th>Phone</th><th>Subject</th><th>Message</th><th>Status</th><th>Submitted</th><th></th>
            </tr></thead>
            <tbody>
              {cPaginated.length === 0
                ? <tr><td colSpan={8} className="adm-empty-cell">No records match your filters.</td></tr>
                : cPaginated.map(c => (
                  <tr key={c._id}>
                    <td>{c.name || '—'}</td>
                    <td><a href={`mailto:${c.email}`} className="adm-lnk">{c.email}</a></td>
                    <td>{c.phone ? <a href={`tel:${c.phone}`} className="adm-lnk">{c.phone}</a> : '—'}</td>
                    <td>{c.subject || '—'}</td>
                    <td className="adm-trunc" title={c.message}>{c.message || '—'}</td>
                    <td><span className="adm-status" style={{ borderColor: STATUS_COLORS[c.status || 'new'], color: STATUS_COLORS[c.status || 'new'] }}>{(c.status || 'new').toUpperCase()}</span></td>
                    <td className="adm-nowrap">{fmtDate(c.created_at)}</td>
                    <td>
                      <div className="adm-row-btns">
                        <button className="adm-icon-btn" title="View" onClick={() => setViewMsg(c)}><Icon paths={IC.view} size={14} /></button>
                        <button className="adm-icon-btn adm-icon-btn-warn" title="Edit" onClick={() => { setEditMsg(c); setEditStatus(c.status || 'new'); }}><Icon paths={IC.edit} size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      }

      {cTotalPages > 1 && (
        <div className="adm-pager">
          <button className="adm-btn adm-btn-ghost adm-btn-xs" disabled={cPage === 0} onClick={() => setCPage(p => p - 1)}>← Prev</button>
          <span>Page {cPage + 1} of {cTotalPages}</span>
          <button className="adm-btn adm-btn-ghost adm-btn-xs" disabled={cPage >= cTotalPages - 1} onClick={() => setCPage(p => p + 1)}>Next →</button>
        </div>
      )}
    </div>
  );

  const renderUsers = () => (
    <div className="adm-section">
      <div className="adm-section-hd">
        <h2>Normal Users</h2>
        <p>All registered users on the InvFly platform.</p>
      </div>

      <div className="adm-table-bar" style={{ marginBottom: 16 }}>
        <div className="adm-search-row">
          <input className="adm-search-input" placeholder="Search by name, email, or phone…"
            value={uSearch} onChange={e => setUSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && fetchUsers(0, uSearch)} />
          <button className="adm-btn adm-btn-accent adm-btn-xs" onClick={() => fetchUsers(0, uSearch)}>Search</button>
          <button className="adm-btn adm-btn-ghost adm-btn-xs" onClick={() => { setUSearch(''); fetchUsers(0, ''); }}>Clear</button>
        </div>
        <span className="adm-pill">{uTotal} total user{uTotal !== 1 ? 's' : ''}</span>
      </div>

      {uLoading
        ? <div className="adm-spin-wrap"><div className="adm-spinner" /></div>
        : <div className="adm-table-wrap">
          <table className="adm-table">
            <thead><tr>
              <th>User</th><th>Email</th><th>Phone</th><th>Auth</th>
              <th>Status</th><th>Verified</th><th>Courses</th><th>Last Login</th><th>Last Login IP</th><th>Joined</th>
            </tr></thead>
            <tbody>
              {users.length === 0
                ? <tr><td colSpan={10} className="adm-empty-cell">No users found.</td></tr>
                : users.map(u => (
                  <tr key={u._id}>
                    <td>
                      <div className="adm-user-cell">
                        <span className="adm-avatar-sm">{initials(u.name || u.email)}</span>
                        <span>{u.name || '—'}</span>
                      </div>
                    </td>
                    <td className="adm-trunc" style={{ maxWidth: 180 }}><a href={`mailto:${u.email}`} className="adm-lnk">{u.email}</a></td>
                    <td>{u.phone || '—'}</td>
                    <td><span className="adm-tag">{u.auth_type || 'local'}</span></td>
                    <td>
                      <button
                        className={`adm-status-toggle ${u.status === 'inactive' ? 'adm-status-inactive' : 'adm-status-active'}`}
                        disabled={uStatusSaving === u._id}
                        onClick={() => handleToggleUserStatus(u)}
                        title={u.status === 'active' ? 'Click to deactivate' : 'Click to activate'}>
                        {uStatusSaving === u._id ? '…' : (u.status === 'active' ? 'Active' : 'Inactive')}
                      </button>
                    </td>
                    <td>
                      {u.is_email_verified
                        ? <span className="adm-tag adm-tag-green">Verified</span>
                        : <span className="adm-tag adm-tag-red">Unverified</span>}
                    </td>
                    <td><span className="adm-pill adm-pill-sm">{(u.subscribed_courses || []).length}</span></td>
                    <td className="adm-nowrap">{fmtDate(u.last_login_on)}</td>
                    <td className="adm-nowrap">{u.last_login_ip_address || '—'}</td>
                    <td className="adm-nowrap">{fmtDate(u.created_at)}</td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      }

      {uTotalPages > 1 && (
        <div className="adm-pager">
          <button className="adm-btn adm-btn-ghost adm-btn-xs" disabled={uPage === 0} onClick={() => fetchUsers(uPage - 1)}>← Prev</button>
          <span>Page {uPage + 1} of {uTotalPages}</span>
          <button className="adm-btn adm-btn-ghost adm-btn-xs" disabled={uPage >= uTotalPages - 1} onClick={() => fetchUsers(uPage + 1)}>Next →</button>
        </div>
      )}
    </div>
  );

  const renderProfile = () => (
    <div className="adm-section">
      <div className="adm-section-hd">
        <h2>My Profile</h2>
        <p>Manage your admin account details and change your password.</p>
      </div>

      <div className="adm-profile-grid">
        {/* Identity card */}
        <div className="adm-card adm-profile-id-card">
          <div className="adm-profile-avatar">{initials(adminInfo?.name || adminInfo?.email)}</div>
          <div className="adm-profile-name">{adminInfo?.name || adminInfo?.email?.split('@')[0] || '—'}</div>
          <div className="adm-profile-email">{adminInfo?.email || '—'}</div>
          <span className="adm-tag adm-tag-accent">{(adminInfo?.roles || ['superadmin']).map(r => ROLE_LABELS[r] || r).join(', ').toUpperCase()}</span>
          {adminInfo?.last_login_on && (
            <div className="adm-profile-meta">
              <span>Last login</span>
              <strong>{fmtDate(adminInfo.last_login_on)}</strong>
            </div>
          )}
          {adminInfo?.last_login_ip_address && (
            <div className="adm-profile-meta">
              <span>From IP</span>
              <strong>{adminInfo.last_login_ip_address}</strong>
            </div>
          )}
        </div>

        {/* Edit profile */}
        <div className="adm-card">
          <h3 className="adm-card-title">Edit Profile</h3>
          <form onSubmit={handleProfileSave} className="adm-form">
            <label className="adm-field adm-field-lg">
              <span>Display Name</span>
              <input value={profForm.name} onChange={e => setProfForm(p => ({ ...p, name: e.target.value }))}
                maxLength={80} placeholder="Your name (optional)" />
            </label>
            <label className="adm-field adm-field-lg">
              <span>Email Address</span>
              <input type="email" value={profForm.email} onChange={e => setProfForm(p => ({ ...p, email: e.target.value }))}
                required placeholder="admin@example.com" />
            </label>
            <button type="submit" className="adm-btn adm-btn-accent" disabled={profSaving}>
              {profSaving ? 'Saving…' : 'Save Changes'}
            </button>
          </form>
        </div>

        {/* Change password */}
        <div className="adm-card">
          <h3 className="adm-card-title">Change Password</h3>
          <form onSubmit={handlePasswordChange} className="adm-form">
            {[
              { field: 'current_password', label: 'Current Password', key: 'cur' },
              { field: 'new_password', label: 'New Password', key: 'nw' },
              { field: 'confirm_password', label: 'Confirm New Password', key: 'cnf' },
            ].map(({ field, label, key }) => (
              <label key={field} className="adm-field adm-field-lg">
                <span>{label}</span>
                <div className="adm-pw-wrap">
                  <input type={showPw[key] ? 'text' : 'password'} value={pwForm[field]}
                    onChange={e => setPwForm(p => ({ ...p, [field]: e.target.value }))}
                    required={field !== 'confirm_password'}
                    minLength={field !== 'current_password' ? 8 : undefined}
                    placeholder="••••••••" />
                  <button type="button" className="adm-pw-eye" tabIndex={-1}
                    onClick={() => setShowPw(p => ({ ...p, [key]: !p[key] }))}>
                    <Icon paths={showPw[key] ? IC.eyeOff : IC.eye} size={16} />
                  </button>
                </div>
              </label>
            ))}
            <button type="submit" className="adm-btn adm-btn-accent" disabled={pwSaving}>
              {pwSaving ? 'Updating…' : 'Update Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );

  const renderAdminUsers = () => (
    <div className="adm-section">
      <div className="adm-section-hd">
        <div>
          <h2>Admin Users</h2>
          <p>Manage administrator accounts on this platform.</p>
        </div>
        <button className="adm-btn adm-btn-accent" onClick={() => setAuShowCreate(true)}>
          + Add Admin User
        </button>
      </div>


      {auLoading ? (
        <div className="adm-empty">Loading admin users…</div>
      ) : (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Name</th><th>Email</th><th>Role</th><th>Last Login</th><th>Last Login IP</th><th>Created</th>
              </tr>
            </thead>
            <tbody>
              {adminUsers.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted)' }}>No admin users found</td></tr>
              ) : adminUsers.map(au => {
                const isMe = adminInfo && String(au._id) === String(adminInfo.id || adminInfo._id);
                return (
                  <tr key={au._id} className={isMe ? 'adm-row-me' : ''}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="adm-avatar-sm" style={{ background: 'var(--accent)', color: '#fff', fontSize: 11 }}>
                          {(au.name || au.email || '?').charAt(0).toUpperCase()}
                        </span>
                        <span>{au.name || <em style={{ color: 'var(--muted)' }}>No name set</em>}</span>
                        {isMe && <span className="adm-tag" style={{ fontSize: 10, background: 'var(--accent-2)', color: '#fff', marginLeft: 4 }}>You</span>}
                      </div>
                    </td>
                    <td>{au.email}</td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {(au.roles || []).map(r => (
                          <span key={r} className="adm-tag adm-tag-accent" style={{ textTransform: 'uppercase', fontSize: 10 }}>{ROLE_LABELS[r] || r}</span>
                        ))}
                      </div>
                    </td>
                    <td>{fmtDate(au.last_login_on)}</td>
                    <td>{au.last_login_ip_address || '—'}</td>
                    <td>{fmtDate(au.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {auShowCreate && (
        <div className="adm-overlay" onClick={() => setAuShowCreate(false)}>
          <div className="adm-dialog" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="adm-dlg-hd">
              <h3>Add Admin User</h3>
              <button className="adm-dlg-close" onClick={() => setAuShowCreate(false)}>✕</button>
            </div>
            <form onSubmit={handleCreateAdminUser}>
              <div className="adm-dlg-body">
                <label className="adm-field adm-field-lg">
                  <span>Display Name <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(optional)</span></span>
                  <input value={auCreateForm.name}
                    onChange={e => setAuCreateForm(p => ({ ...p, name: e.target.value }))}
                    maxLength={80} placeholder="Full name" />
                </label>
                <label className="adm-field adm-field-lg">
                  <span>Email Address *</span>
                  <input type="email" value={auCreateForm.email} required
                    onChange={e => setAuCreateForm(p => ({ ...p, email: e.target.value }))}
                    placeholder="admin@invfly.com" />
                </label>
                <label className="adm-field adm-field-lg">
                  <span>Password * <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(min 8 chars)</span></span>
                  <input type="password" value={auCreateForm.password} required minLength={8}
                    onChange={e => setAuCreateForm(p => ({ ...p, password: e.target.value }))}
                    placeholder="••••••••" autoComplete="new-password" />
                </label>
                <label className="adm-field adm-field-lg">
                  <span>Roles <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(select at least one)</span></span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                    {ALL_ROLES.map(r => (
                      <label key={r} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 400, fontSize: 13 }}>
                        <input
                          type="checkbox"
                          checked={auCreateForm.roles.includes(r)}
                          onChange={e => setAuCreateForm(p => ({
                            ...p,
                            roles: e.target.checked ? [...p.roles, r] : p.roles.filter(x => x !== r)
                          }))}
                          style={{ width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--accent)' }}
                        />
                        {ROLE_LABELS[r] || r}
                      </label>
                    ))}
                  </div>
                </label>
              </div>
              <div className="adm-dlg-ft">
                <button type="button" className="adm-btn adm-btn-ghost" onClick={() => setAuShowCreate(false)}>Cancel</button>
                <button type="submit" className="adm-btn adm-btn-accent" disabled={auCreateSaving}>
                  {auCreateSaving ? 'Creating…' : 'Create Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  const renderComingSoon = (title, desc) => (<div className="adm-section">
    <div className="adm-coming-soon">
      <div className="adm-coming-icon"><Icon paths={IC.analytics} size={36} /></div>
      <h2>{title}</h2>
      <p>{desc}</p>
      <span className="adm-pill">Coming soon</span>
    </div>
  </div>
  );

  const renderCourses = () => (
    <div className="adm-section">
      <div className="adm-section-hd">
        <div>
          <h2>Courses</h2>
          <p>Manage course catalogue, pricing, curriculum and visibility.</p>
        </div>
        <button className="adm-btn adm-btn-accent" onClick={openCsCreate}>+ Add Course</button>
      </div>

      {csLoading ? (
        <div className="adm-spin-wrap"><div className="adm-spinner" /></div>
      ) : (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead><tr>
              <th>#</th><th>Course</th><th>Category</th><th>Price</th>
              <th>Active</th><th>Enrollable</th><th>Modules</th><th>Actions</th>
            </tr></thead>
            <tbody>
              {courseList.length === 0 ? (
                <tr><td colSpan={8} className="adm-empty-cell">No courses yet. Click "Add Course" to get started.</td></tr>
              ) : courseList.map(c => (
                <tr key={c._id}>
                  <td style={{ fontWeight: 600, color: 'var(--text-3)' }}>{c.course_id}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <CourseIcon icon={c.icon} />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{c.title}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{c.course_code}</div>
                      </div>
                    </div>
                  </td>
                  <td><span className="adm-tag">{CAT_LABEL[c.category] || c.category}</span></td>
                  <td style={{ fontWeight: 600 }}>₹{c.price}</td>
                  <td>
                    <span className={`adm-tag ${c.is_active ? 'adm-tag-green' : 'adm-tag-red'}`}>
                      {c.is_active ? 'Active' : 'Hidden'}
                    </span>
                  </td>
                  <td>
                    <span className={`adm-tag ${c.is_enrollable ? 'adm-tag-green' : 'adm-tag-red'}`}>
                      {c.is_enrollable ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td><span className="adm-pill adm-pill-sm">{(c.modules || []).length}</span></td>
                  <td>
                    <div className="adm-row-btns">
                      <button className="adm-icon-btn" title="View Details" onClick={() => setCsViewTarget(c)}>
                        <Icon paths="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" size={14} />
                      </button>
                      <button className="adm-icon-btn" title="Manage Modules" onClick={() => setCsModulesTarget(c)}>
                        <Icon paths="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2M9 12h6M9 16h4" size={14} />
                      </button>
                      <button className="adm-icon-btn" title="Edit" onClick={() => openCsEdit(c)}>
                        <Icon paths={IC.edit} size={14} />
                      </button>
                      <button className="adm-icon-btn adm-icon-btn-danger" title="Delete" onClick={() => setCsDeleteTarget(c)}>
                        <Icon paths="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Course form dialog */}
      {csShowForm && (
        <div className="adm-overlay" onClick={() => setCsShowForm(false)}>
          <div className="adm-dialog adm-dialog-wide" onClick={e => e.stopPropagation()}>
            <div className="adm-dlg-hd">
              <h3>{csEditId ? 'Edit Course' : 'Add Course'}</h3>
              <button className="adm-dlg-close" onClick={() => setCsShowForm(false)}>✕</button>
            </div>
            <form onSubmit={handleCsSave}>
              <div className="adm-dlg-body adm-cs-form">
                <div className="adm-cs-cols">
                  {/* Left col */}
                  <div className="adm-cs-col">
                    <div className="adm-cs-section-title">Basic Info</div>
                    {csEditId && csForm.course_id && (
                      <label className="adm-field">
                        <span>Course ID <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>— auto-assigned, read-only</span></span>
                        <input value={csForm.course_id || ''} readOnly style={{ opacity: 0.6, cursor: 'not-allowed' }} />
                      </label>
                    )}
                    {[
                      ['Course Code *', 'course_code', 'text', 'e.g. technical-analysis'],
                      ['Icon (emoji)', 'icon', 'text', '📈'],
                      ['Title *', 'title', 'text', 'Course title'],
                      ['Tagline', 'tagline', 'text', 'Short one-liner'],
                      ['Target Audience', 'target_audience', 'text', 'Beginners & students'],
                      ['Duration', 'duration', 'text', '12 Hours'],
                      ['Price (₹) *', 'price', 'number', '499'],
                    ].map(([label, field, type, ph]) => (
                      <label key={field} className="adm-field">
                        <span>{label}</span>
                        <input type={type} value={csForm[field]} placeholder={ph}
                          required={label.includes('*')}
                          min={type === 'number' ? 0 : undefined}
                          onChange={e => setCsForm(p => ({ ...p, [field]: e.target.value }))} />
                      </label>
                    ))}
                    <label className="adm-field">
                      <span>Category</span>
                      <select value={csForm.category} onChange={e => setCsForm(p => ({ ...p, category: e.target.value }))}>
                        {CATEGORIES.map(c => <option key={c} value={c}>{CAT_LABEL[c]}</option>)}
                      </select>
                    </label>
                    <div style={{ display: 'flex', gap: 24, marginTop: 4 }}>
                      {[['is_active', 'Active (visible on site)'], ['is_enrollable', 'Enrollable (can pay)']].map(([field, label]) => (
                        <label key={field} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 13, fontWeight: 400 }}>
                          <input type="checkbox" checked={csForm[field]} onChange={e => setCsForm(p => ({ ...p, [field]: e.target.checked }))} style={{ width: 14, height: 14, accentColor: 'var(--accent)' }} />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Right col */}
                  <div className="adm-cs-col">
                    <div className="adm-cs-section-title">Content</div>
                    <label className="adm-field">
                      <span>Overview</span>
                      <textarea rows={4} value={csForm.overview} placeholder="Detailed description…"
                        onChange={e => setCsForm(p => ({ ...p, overview: e.target.value }))} />
                    </label>
                    <label className="adm-field">
                      <span>Learning Highlights</span>
                      <ListEditor items={csForm.learn_highlights}
                        onChange={v => setCsForm(p => ({ ...p, learn_highlights: v }))}
                        placeholder="e.g. Chart reading" />
                    </label>
                    <label className="adm-field">
                      <span>Outcome Summary <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>— single line shown on card</span></span>
                      <input value={csForm.outcome_summary} placeholder="e.g. Confidence in reading price action."
                        onChange={e => setCsForm(p => ({ ...p, outcome_summary: e.target.value }))} />
                    </label>
                    <label className="adm-field">
                      <span>Full Outcomes</span>
                      <ListEditor items={csForm.outcomes}
                        onChange={v => setCsForm(p => ({ ...p, outcomes: v }))}
                        placeholder="e.g. Understand how markets work" />
                    </label>
                    <div className="adm-field">
                      <span>Cover Image <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>— shown on course cards</span></span>
                      {csForm.cover_image ? (
                        <div style={{ position: 'relative', width: '100%', height: 150, borderRadius: 'var(--rs)', overflow: 'hidden', border: '1px solid var(--border)' }}>
                          <img src={csForm.cover_image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                          <button type="button" onClick={() => setCsForm(p => ({ ...p, cover_image: '' }))}
                            style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 9px', fontSize: 12, cursor: 'pointer' }}>
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div style={{ width: '100%', height: 150, borderRadius: 'var(--rs)', border: '1px dashed var(--border-l)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontSize: 13 }}>
                          No cover yet
                        </div>
                      )}
                      <label className="adm-btn adm-btn-ghost" style={{ justifyContent: 'center', cursor: 'pointer', marginTop: 8 }}>
                        {csCoverUploading ? 'Uploading…' : 'Upload Cover'}
                        <input type="file" hidden accept="image/jpeg,image/png,image/webp,image/gif" disabled={csCoverUploading} onChange={handleCsCoverChange} />
                      </label>
                    </div>
                    <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>
                      💡 Curriculum modules are managed via the <strong>Modules</strong> button in the course table.
                    </p>
                  </div>
                </div>
              </div>
              <div className="adm-dlg-ft">
                <button type="button" className="adm-btn adm-btn-ghost" onClick={() => setCsShowForm(false)}>Cancel</button>
                <button type="submit" className="adm-btn adm-btn-accent" disabled={csSaving}>
                  {csSaving ? 'Saving…' : (csEditId ? 'Save Changes' : 'Create Course')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation dialog */}
      {csDeleteTarget && (
        <div className="adm-overlay" onClick={() => setCsDeleteTarget(null)}>
          <div className="adm-dialog adm-dialog-sm" onClick={e => e.stopPropagation()}>
            <div className="adm-dlg-hd">
              <h3>Delete Course</h3>
              <button className="adm-dlg-close" onClick={() => setCsDeleteTarget(null)}>✕</button>
            </div>
            <div className="adm-dlg-body">
              <p style={{ color: 'var(--text-2)', fontSize: 14 }}>
                Are you sure you want to delete <strong style={{ color: 'var(--text)' }}>{csDeleteTarget.title}</strong>?
                This action cannot be undone.
              </p>
            </div>
            <div className="adm-dlg-ft">
              <button className="adm-btn adm-btn-ghost" onClick={() => setCsDeleteTarget(null)}>Cancel</button>
              <button className="adm-btn adm-btn-danger" onClick={handleCsDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Course detail view dialog */}
      {csViewTarget && (() => {
        const c = csViewTarget;
        return (
          <div className="adm-overlay" onClick={() => setCsViewTarget(null)}>
            <div className="adm-dialog adm-dialog-wide adm-dialog-tall" onClick={e => e.stopPropagation()}>
              <div className="adm-dlg-hd">
                <h3 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <CourseIcon icon={c.icon} />
                  {c.title}
                  <span className="adm-tag" style={{ marginLeft: 4 }}>{CAT_LABEL[c.category] || c.category}</span>
                </h3>
                <button className="adm-dlg-close" onClick={() => setCsViewTarget(null)}>✕</button>
              </div>
              <div className="adm-dlg-body" style={{ gap: 18 }}>
                {/* Meta strip */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                  {[
                    ['Course ID', `#${c.course_id}`],
                    ['Code', c.course_code],
                    ['Price', c.price === 0 ? 'Free' : `₹${c.price}`],
                    ['Duration', c.duration || '—'],
                    ['Modules', (c.modules || []).length],
                    ['Active', c.is_active ? '✓ Yes' : '✗ No'],
                    ['Enrollable', c.is_enrollable ? '✓ Yes' : '✗ No'],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 90 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)' }}>{k}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{v}</span>
                    </div>
                  ))}
                </div>

                {c.tagline && <p style={{ fontSize: 14, color: 'var(--text-2)', fontStyle: 'italic', margin: 0 }}>{c.tagline}</p>}

                {c.target_audience && (
                  <div>
                    <div className="adm-cs-section-title" style={{ marginBottom: 6 }}>Target Audience</div>
                    <p style={{ fontSize: 13, color: 'var(--text-2)', margin: 0 }}>{c.target_audience}</p>
                  </div>
                )}

                {c.overview && (
                  <div>
                    <div className="adm-cs-section-title" style={{ marginBottom: 6 }}>Overview</div>
                    <p style={{ fontSize: 13, color: 'var(--text-2)', margin: 0, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{c.overview}</p>
                  </div>
                )}

                {(c.learn_highlights || []).length > 0 && (
                  <div>
                    <div className="adm-cs-section-title" style={{ marginBottom: 8 }}>Learning Highlights</div>
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {c.learn_highlights.map((h, i) => (
                        <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: 'var(--text-2)' }}>
                          <span style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 1 }}>✓</span>{h}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {c.outcome_summary && (
                  <div style={{ background: 'var(--surface-2,var(--bg))', border: '1px solid var(--border)', borderRadius: 'var(--r)', padding: '12px 16px' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)' }}>Outcome Summary</span>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text)', fontWeight: 600 }}>{c.outcome_summary}</p>
                  </div>
                )}

                {(c.outcomes || []).length > 0 && (
                  <div>
                    <div className="adm-cs-section-title" style={{ marginBottom: 8 }}>Full Outcomes</div>
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {c.outcomes.map((o, i) => (
                        <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: 'var(--text-2)' }}>
                          <span style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 1 }}>→</span>{o}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {(c.modules || []).length > 0 && (
                  <div>
                    <div className="adm-cs-section-title" style={{ marginBottom: 10 }}>Curriculum — {c.modules.length} Module{c.modules.length !== 1 ? 's' : ''}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {c.modules.map((mod, mi) => (
                        <div key={mi} className="adm-module-card" style={{ gap: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--accent)', minWidth: 60 }}>Module {mi + 1}</span>
                            <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)' }}>{mod.title}</span>
                            <span style={{ fontSize: 11, color: 'var(--text-3)', marginLeft: 'auto' }}>{(mod.sessions || []).length} session{(mod.sessions || []).length !== 1 ? 's' : ''}</span>
                          </div>
                          {(mod.sessions || []).length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 8, borderLeft: '2px solid var(--border)' }}>
                              {mod.sessions.map((s, si) => (
                                <div key={si} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: 'var(--text-2)' }}>
                                  <span style={{ color: 'var(--text-3)', minWidth: 20, flexShrink: 0 }}>{s.number || (si + 1)}.</span>
                                  <span style={{ flex: 1 }}>{s.title}</span>
                                  {s.duration && <span style={{ color: 'var(--text-3)', flexShrink: 0 }}>{s.duration}</span>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="adm-dlg-ft">
                <button className="adm-btn adm-btn-ghost" onClick={() => { setCsViewTarget(null); setCsModulesTarget(c); }}>Edit Modules</button>
                <button className="adm-btn adm-btn-ghost" style={{ marginLeft: 8 }} onClick={() => { setCsViewTarget(null); openCsEdit(c); }}>Edit Course</button>
                <button className="adm-btn adm-btn-accent" style={{ marginLeft: 'auto' }} onClick={() => setCsViewTarget(null)}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Module editor dialog */}
      {csModulesTarget && (
        <ModuleEditor
          course={csModulesTarget}
          onClose={() => setCsModulesTarget(null)}
          onSaved={(courseId, modules) => {
            setCourseList(prev => prev.map(c => c._id === courseId ? { ...c, modules } : c));
            setCsModulesTarget(null);
          }}
          showToast={showToast}
        />
      )}
    </div>
  );

  // ── Stocks section ───────────────────────────────────────────────────────
  const renderStocks = () => (
    <div className="adm-section">
      <div className="adm-section-hd">
        <h2>Stocks</h2>
        <p>Manage the NSE stocks master list.</p>
      </div>

      <div className="adm-table-bar" style={{ marginBottom: 16 }}>
        <div className="adm-search-row">
          <input className="adm-search-input" placeholder="Search symbol or company…"
            value={stSearch} onChange={e => setStSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && fetchStocks(0, stSearch)} />
          <button className="adm-btn adm-btn-accent adm-btn-xs" onClick={() => fetchStocks(0, stSearch)}>Search</button>
          <button className="adm-btn adm-btn-ghost adm-btn-xs" onClick={() => { setStSearch(''); fetchStocks(0, ''); }}>Clear</button>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className="adm-pill">{stTotal} stocks</span>
          <button className="adm-btn adm-btn-accent adm-btn-xs" onClick={openStockAdd}>+ Add Stock</button>
        </div>
      </div>

      {stLoading
        ? <div className="adm-spin-wrap"><div className="adm-spinner" /></div>
        : <div className="adm-table-wrap">
          <table className="adm-table">
            <thead><tr>
              <th>Symbol</th><th>Company Name</th><th>Sector</th>
              <th>Exchange</th><th>Market Cap</th><th>Status</th><th>Actions</th>
            </tr></thead>
            <tbody>
              {stockList.length === 0
                ? <tr><td colSpan={7} className="adm-empty-cell">No stocks found.</td></tr>
                : stockList.map(s => (
                  <tr key={s.id}>
                    <td><strong style={{ fontFamily: 'monospace', color: 'var(--accent)' }}>{s.symbol}</strong></td>
                    <td>{s.company_name}</td>
                    <td>{s.sector || '—'}</td>
                    <td><span className="adm-tag">{s.exchange}</span></td>
                    <td>{s.market_cap_category || '—'}</td>
                    <td>
                      <button
                        className={`adm-status-toggle ${s.is_active ? 'adm-status-active' : 'adm-status-inactive'}`}
                        disabled={stToggling === s.id}
                        onClick={() => handleStockToggle(s)}
                        title={s.is_active ? 'Click to deactivate' : 'Click to activate'}>
                        {stToggling === s.id ? '…' : (s.is_active ? 'Active' : 'Inactive')}
                      </button>
                    </td>
                    <td>
                      <div className="adm-row-btns">
                        <button className="adm-icon-btn adm-icon-btn-warn" title="Edit" onClick={() => openStockEdit(s)}>
                          <Icon paths={IC.edit} size={14} />
                        </button>
                        <button className="adm-icon-btn adm-icon-btn-danger" title="Delete" onClick={() => setStDeleteTarget(s)}>
                          <Icon paths="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      }

      {Math.ceil(stTotal / ST_LIMIT) > 1 && (
        <div className="adm-pager">
          <button className="adm-btn adm-btn-ghost adm-btn-xs" disabled={stPage === 0} onClick={() => fetchStocks(stPage - 1)}>← Prev</button>
          <span>Page {stPage + 1} of {Math.ceil(stTotal / ST_LIMIT)}</span>
          <button className="adm-btn adm-btn-ghost adm-btn-xs" disabled={stPage >= Math.ceil(stTotal / ST_LIMIT) - 1} onClick={() => fetchStocks(stPage + 1)}>Next →</button>
        </div>
      )}

      {/* Add/Edit modal */}
      {stShowForm && (
        <div className="adm-overlay" onClick={() => setStShowForm(false)}>
          <div className="adm-dialog adm-dialog-sm" onClick={e => e.stopPropagation()}>
            <div className="adm-dlg-hd">
              <h3>{stEditTarget ? 'Edit Stock' : 'Add Stock'}</h3>
              <button className="adm-dlg-close" onClick={() => setStShowForm(false)}>✕</button>
            </div>
            <form onSubmit={handleStockSave}>
              <div className="adm-dlg-body">
                {/* Company Name doubles as the Yahoo Finance search when adding */}
                <label className="adm-field adm-field-lg">
                  <span>Company Name <span style={{ color: 'var(--red)' }}>*</span></span>
                  <input
                    value={stForm.company_name}
                    placeholder={stEditTarget ? '' : 'Type a company name, e.g. Reliance, TCS, Tata Steel…'}
                    onChange={e => {
                      const v = e.target.value;
                      setStForm(p => ({ ...p, company_name: v }));
                      if (!stEditTarget) searchYahooStocks(v);
                    }}
                    required
                    autoComplete="off"
                  />
                  {!stEditTarget && (stYahooSearching || stYahooResults.length > 0) && (
                    <div style={{ border: '1px solid var(--border)', borderRadius: 6, marginTop: 6, maxHeight: 200, overflowY: 'auto', background: 'var(--surface-2, #0e1116)' }}>
                      {stYahooSearching && <div style={{ padding: '8px 10px', fontSize: 12, color: 'var(--text-3)' }}>Searching…</div>}
                      {!stYahooSearching && stYahooResults.length === 0 && <div style={{ padding: '8px 10px', fontSize: 12, color: 'var(--text-3)' }}>No NSE matches found.</div>}
                      {stYahooResults.map(r => (
                        <button type="button" key={r.symbol} onClick={() => applyYahooResult(r)}
                          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', background: 'none', border: 'none', borderBottom: '1px solid var(--border)', color: 'var(--text)', cursor: 'pointer', fontSize: 12.5 }}>
                          <strong style={{ fontFamily: 'monospace', color: 'var(--accent)' }}>{r.symbol}</strong> — {r.company_name}{r.sector ? ` · ${r.sector}` : ''}
                        </button>
                      ))}
                    </div>
                  )}
                </label>

                <label className="adm-field adm-field-lg">
                  <span>Symbol <span style={{ color: 'var(--red)' }}>*</span></span>
                  <input
                    value={stForm.symbol}
                    placeholder={stEditTarget ? '' : 'Auto-filled from the selected company'}
                    onChange={e => setStForm(p => ({ ...p, symbol: e.target.value }))}
                    required
                    readOnly={!!stEditTarget}
                    style={stEditTarget ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                  />
                </label>

                {[['Sector', 'sector'], ['Exchange', 'exchange'], ['Market Cap Category', 'market_cap_category']].map(([lbl, key]) => (
                  <label key={key} className="adm-field adm-field-lg">
                    <span>{lbl}</span>
                    <input value={stForm[key]} onChange={e => setStForm(p => ({ ...p, [key]: e.target.value }))} />
                  </label>
                ))}

                {!stEditTarget && (
                  <p style={{ margin: 0, fontSize: 11.5, color: 'var(--text-3)' }}>
                    The symbol is verified against Yahoo Finance (NSE) on save — invalid symbols are rejected.
                  </p>
                )}
              </div>
              <div className="adm-dlg-ft">
                <button type="button" className="adm-btn adm-btn-ghost" onClick={() => setStShowForm(false)}>Cancel</button>
                <button type="submit" className="adm-btn adm-btn-accent" disabled={stSaving}>{stSaving ? 'Saving…' : stEditTarget ? 'Update' : 'Add Stock'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete stock confirm */}
      {stDeleteTarget && (
        <div className="adm-overlay" onClick={() => !stDeleting && setStDeleteTarget(null)}>
          <div className="adm-dialog adm-dialog-sm" onClick={e => e.stopPropagation()}>
            <div className="adm-dlg-hd">
              <h3>Delete Stock</h3>
              <button className="adm-dlg-close" onClick={() => setStDeleteTarget(null)}>✕</button>
            </div>
            <div className="adm-dlg-body">
              <p style={{ margin: 0, fontSize: 13 }}>
                Delete <strong>{stDeleteTarget.symbol}</strong> ({stDeleteTarget.company_name})?
                This removes the stock and its paper-trading orders &amp; positions. Any open holdings are refunded to users at the last reported price. This cannot be undone.
              </p>
            </div>
            <div className="adm-dlg-ft">
              <button className="adm-btn adm-btn-ghost" onClick={() => setStDeleteTarget(null)} disabled={stDeleting}>Cancel</button>
              <button className="adm-btn adm-btn-danger" onClick={handleStockDelete} disabled={stDeleting}>{stDeleting ? 'Deleting…' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const MD_HIST_PER_PAGE = 12;
  const renderMarketData = () => {
    const mdHistPages = Math.max(1, Math.ceil(mdHistory.length / MD_HIST_PER_PAGE));
    const mdHistRows  = mdHistory.slice(mdHistPage * MD_HIST_PER_PAGE, (mdHistPage + 1) * MD_HIST_PER_PAGE);
    // Re-derive the selected job from the polled list so the detail modal stays live.
    const mjSel = mjSelId ? mjJobs.find(j => j._id === mjSelId) : null;
    return (
    <div className="adm-section">
      <div className="adm-section-hd">
        <h2>Market Data</h2>
        <p>Monitor live market quotes and trigger jobs to fetch the latest/required period market data.</p>
      </div>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
        {[['monitor', 'Live Data Monitor'], ['jobs', 'Manual Jobs'], ['provider', 'Data Provider']].map(([id, label]) => (
          <button key={id}
            className={`adm-btn adm-btn-xs ${mdTab === id ? 'adm-btn-accent' : 'adm-btn-ghost'}`}
            onClick={() => { setMdTab(id); updateTabParam(id); }}>
            {label}
          </button>
        ))}
      </div>

      {/* Data Provider: choose the active market-data source (Yahoo stored candles
          vs Fyers live) and manage the Fyers connection. The two tabs above read
          from stored candles regardless of this setting — the Live Data Monitor
          reports QuestDB row counts, and Manual Jobs drives the Python fetcher. */}
      {mdTab === 'provider' && (
        <AdminMarketData token={localStorage.getItem('adminToken')} />
      )}

      {mdTab === 'monitor' && (
        <>
          {/* Stocks list — market data opens in a dialog via the Actions icon */}
          <div className="adm-card">
            <div className="adm-table-bar" style={{ marginBottom: 12 }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>Stocks</span>
              <button className="adm-btn adm-btn-ghost adm-btn-xs" onClick={fetchMdLiveData} disabled={mdLoading}>
                {mdLoading ? 'Loading…' : '↻ Refresh'}
              </button>
            </div>
            {mdLoading
              ? <div className="adm-spin-wrap"><div className="adm-spinner" /></div>
              : mdQuotes.length === 0
                ? <div className="adm-empty-cell" style={{ padding: 20, textAlign: 'center' }}>No stock data available yet. Trigger "Fetch Live Quotes" first.</div>
                : <div className="adm-table-wrap">
                    <table className="adm-table">
                      <thead>
                        <tr>
                          <th>Symbol</th><th>Company</th><th>Data Points</th><th>Last Updated (IST)</th><th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mdQuotes.map(q => (
                          <tr key={q.symbol}>
                            <td><strong>{q.symbol}</strong></td>
                            <td className="adm-trunc">{q.company_name || '—'}</td>
                            <td>
                              <span className="adm-pill" style={q.total_rows ? undefined : { opacity: 0.5 }}>{q.total_rows}</span>
                            </td>
                            <td className="adm-nowrap" style={{ fontSize: 11 }}>
                              {q.ts ? new Date(q.ts).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false }) : '—'}
                            </td>
                            <td>
                              <button className="adm-icon-btn" disabled={!q.total_rows}
                                title={q.total_rows ? 'View Market Data' : 'No data captured yet'}
                                onClick={() => fetchMdHistory(q.symbol)}>
                                <Icon paths={IC.view} size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
            }
          </div>

          {/* Market data — opens in a dialog from the Actions view icon */}
          {mdSelSym && (
            <div className="adm-overlay" onClick={closeMdHistory}>
              <div className="adm-dialog adm-dialog-wide adm-dialog-tall" onClick={e => e.stopPropagation()}>
                <div className="adm-dlg-hd">
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    Market Data — {mdSelSym}
                    {!mdHistLoading && mdHistory.length > 0 && <span className="adm-pill">{mdHistory.length} records</span>}
                  </h3>
                  <button className="adm-dlg-close" onClick={closeMdHistory}>✕</button>
                </div>
                <div className="adm-dlg-body" style={{ padding: 0 }}>
                  {mdHistLoading
                    ? <div className="adm-spin-wrap"><div className="adm-spinner" /></div>
                    : mdHistory.length === 0
                      ? <div className="adm-empty-cell" style={{ padding: 24, textAlign: 'center' }}>No market data found.</div>
                      : <div className="adm-table-wrap">
                          <table className="adm-table">
                            <thead>
                              <tr><th>Timestamp (IST)</th><th>LTP</th><th>Open</th><th>High</th><th>Low</th><th>Prev Close</th><th>Volume</th><th>Change</th><th>Chg%</th></tr>
                            </thead>
                            <tbody>
                              {mdHistRows.map(r => (
                                <tr key={r.ts}>
                                  <td className="adm-nowrap" style={{ fontSize: 11 }}>
                                    {new Date(r.ts).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false })}
                                  </td>
                                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>₹{Number(r.ltp).toFixed(2)}</td>
                                  <td>₹{Number(r.open).toFixed(2)}</td>
                                  <td>₹{Number(r.high).toFixed(2)}</td>
                                  <td>₹{Number(r.low).toFixed(2)}</td>
                                  <td>₹{Number(r.prev_close).toFixed(2)}</td>
                                  <td>{Number(r.volume).toLocaleString('en-IN')}</td>
                                  <td className={r.change >= 0 ? 'up' : 'down'}>{r.change >= 0 ? '+' : ''}{Number(r.change).toFixed(2)}</td>
                                  <td className={r.change_pct >= 0 ? 'up' : 'down'}>{Number(r.change_pct).toFixed(2)}%</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                  }
                </div>
                {!mdHistLoading && mdHistory.length > MD_HIST_PER_PAGE && (
                  <div className="adm-dlg-ft" style={{ justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Latest data shown first</span>
                    <div className="adm-pager">
                      <button className="adm-btn adm-btn-ghost adm-btn-xs" disabled={mdHistPage === 0} onClick={() => setMdHistPage(p => p - 1)}>← Prev</button>
                      <span>Page {mdHistPage + 1} of {mdHistPages}</span>
                      <button className="adm-btn adm-btn-ghost adm-btn-xs" disabled={mdHistPage >= mdHistPages - 1} onClick={() => setMdHistPage(p => p + 1)}>Next →</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {mdTab === 'jobs' && (
        <>
          <div className="adm-card" style={{ marginBottom: 20, padding: '14px 18px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 10 }}>
              <div className="adm-field" style={{ minWidth: 130 }}>
                <label style={{ fontSize: 11, color: 'var(--text-3)' }}>From</label>
                <input type="date" value={mjFilter.from} max={mjFilter.to || undefined}
                  onChange={e => setMjFilter(f => ({ ...f, from: e.target.value }))} />
              </div>
              <div className="adm-field" style={{ minWidth: 130 }}>
                <label style={{ fontSize: 11, color: 'var(--text-3)' }}>To</label>
                <input type="date" value={mjFilter.to} min={mjFilter.from || undefined}
                  onChange={e => setMjFilter(f => ({ ...f, to: e.target.value }))} />
              </div>
              <div className="adm-field" style={{ minWidth: 140 }}>
                <label style={{ fontSize: 11, color: 'var(--text-3)' }}>Type</label>
                <select value={mjFilter.job_type} onChange={e => setMjFilter(f => ({ ...f, job_type: e.target.value }))}>
                  <option value="">All types</option>
                  {Object.entries(JOB_TYPE_LABEL).map(([t, label]) => <option key={t} value={t}>{label}</option>)}
                </select>
              </div>
              <button className="adm-btn adm-btn-accent" title="Fetch jobs"
                onClick={() => fetchManualJobs(0)} disabled={mjLoading} style={{ padding: '8px 12px' }}>
                <Icon paths={IC.chevronR} size={16} />
              </button>
              <button className="adm-btn adm-btn-ghost" title="Reset filters to default"
                onClick={resetManualJobsFilters} disabled={mjLoading} style={{ padding: '8px 12px' }}>
                <Icon paths={IC.reset} size={16} />
              </button>
              <div style={{ flex: 1 }} />
              <button className="adm-btn adm-btn-accent" onClick={() => setMjTriggerOpen(true)}>+ Trigger Job</button>
            </div>
          </div>

          <div className="adm-card">
            <div className="adm-table-bar" style={{ marginBottom: 12 }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>
                Jobs <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>({mjTotal})</span>
              </span>
              {mjLoading && <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</span>}
            </div>
            {mjJobs.length === 0
              ? <div className="adm-empty-cell" style={{ padding: 20, textAlign: 'center' }}>No jobs in the selected date range. Adjust the filters and hit fetch, or trigger one with “+ Trigger Job”.</div>
              : <div className="adm-table-wrap">
                  <table className="adm-table">
                    <thead>
                      <tr>
                        <th>Type</th><th>Details</th><th>Status</th><th>Progress</th><th>Rows</th><th>Triggered (IST)</th><th>Info</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mjJobs.map(j => {
                        const st = JOB_STATUS_STYLE[j.status] || JOB_STATUS_STYLE.STARTED;
                        const summary = jobSummary(j);
                        const isBackfill = j.job_type === 'BACKFILL_HISTORICAL_QUOTES';
                        return (
                          <tr key={j._id}>
                            <td><span className="adm-tag">{JOB_TYPE_LABEL[j.job_type] || j.job_type}</span></td>
                            <td className="adm-trunc" title={summary}>{summary}</td>
                            <td><span className="adm-status" style={{ color: st.color, background: st.bg, borderColor: st.color }}>{j.status}</span></td>
                            <td className="adm-nowrap">
                              {isBackfill
                                ? `${j.progress?.done ?? 0}/${j.progress?.total ?? 0}${j.progress?.failed ? ` (${j.progress.failed} failed)` : ''}`
                                : '—'}
                            </td>
                            <td>{(j.rows_written || 0).toLocaleString('en-IN')}</td>
                            <td className="adm-nowrap" style={{ fontSize: 11 }}>
                              {j.created_at ? new Date(j.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false }) : '—'}
                            </td>
                            <td>
                              <button className="adm-icon-btn" title="View details" onClick={() => setMjSelId(j._id)}>
                                <Icon paths={IC.view} size={14} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
            }
            {mjTotal > MJ_LIMIT && (
              <div className="adm-pager" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
                <button className="adm-btn adm-btn-ghost adm-btn-xs" disabled={mjPage === 0 || mjLoading}
                  onClick={() => fetchManualJobs(mjPage - 1)}>← Prev</button>
                <span>Page {mjPage + 1} of {Math.ceil(mjTotal / MJ_LIMIT)}</span>
                <button className="adm-btn adm-btn-ghost adm-btn-xs" disabled={mjPage >= Math.ceil(mjTotal / MJ_LIMIT) - 1 || mjLoading}
                  onClick={() => fetchManualJobs(mjPage + 1)}>Next →</button>
              </div>
            )}
          </div>

          {/* Trigger-job modal */}
          {mjTriggerOpen && (
            <div className="adm-overlay" onClick={() => setMjTriggerOpen(false)}>
              <div className="adm-dialog" onClick={e => e.stopPropagation()}>
                <div className="adm-dlg-hd">
                  <h3>Trigger a Job</h3>
                  <button className="adm-dlg-close" onClick={() => setMjTriggerOpen(false)}>✕</button>
                </div>
                <div className="adm-dlg-body" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div className="adm-field">
                    <label style={{ fontSize: 12, color: 'var(--text-3)' }}>Job Type</label>
                    <select value={mjJobType} onChange={e => setMjJobType(e.target.value)}>
                      {TRIGGERABLE_JOBS.map(({ type }) => <option key={type} value={type}>{JOB_TYPE_LABEL[type]}</option>)}
                    </select>
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--text-3)', margin: 0 }}>
                    {TRIGGERABLE_JOBS.find(t => t.type === mjJobType)?.desc}
                  </p>

                  {mjJobType === 'BACKFILL_HISTORICAL_QUOTES' && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14 }}>
                      <div className="adm-field">
                        <label style={{ fontSize: 12, color: 'var(--text-3)' }}>Scope</label>
                        <select value={bfForm.scope} onChange={e => setBfForm(f => ({ ...f, scope: e.target.value }))}>
                          <option value="SYMBOLS">Specific symbols</option>
                          <option value="ALL">All active stocks</option>
                        </select>
                      </div>
                      {bfForm.scope !== 'ALL' && (
                        <div className="adm-field" style={{ gridColumn: '1 / -1' }}>
                          <label style={{ fontSize: 12, color: 'var(--text-3)' }}>Symbols (comma-separated)</label>
                          <input placeholder="RELIANCE, TCS, INFY"
                            value={bfForm.symbols}
                            onChange={e => setBfForm(f => ({ ...f, symbols: e.target.value }))} />
                        </div>
                      )}
                      <div className="adm-field">
                        <label style={{ fontSize: 12, color: 'var(--text-3)' }}>Interval</label>
                        <select value={bfForm.interval} onChange={e => setBfForm(f => ({ ...f, interval: e.target.value }))}>
                          <option value="1d">Daily (1d)</option>
                          <option value="1wk">Weekly (1wk)</option>
                          <option value="1mo">Monthly (1mo)</option>
                        </select>
                      </div>
                      <div className="adm-field">
                        <label style={{ fontSize: 12, color: 'var(--text-3)' }}>Start date</label>
                        <input type="date" value={bfForm.start_date} max={bfForm.end_date || undefined}
                          onChange={e => setBfForm(f => ({ ...f, start_date: e.target.value }))} />
                      </div>
                      <div className="adm-field">
                        <label style={{ fontSize: 12, color: 'var(--text-3)' }}>End date</label>
                        <input type="date" value={bfForm.end_date} min={bfForm.start_date || undefined}
                          onChange={e => setBfForm(f => ({ ...f, end_date: e.target.value }))} />
                      </div>
                    </div>
                  )}
                </div>
                <div className="adm-dlg-ft" style={{ justifyContent: 'flex-end', gap: 8 }}>
                  <button className="adm-btn adm-btn-ghost" onClick={() => setMjTriggerOpen(false)} disabled={mjTriggering}>Cancel</button>
                  <button className="adm-btn adm-btn-accent" onClick={triggerJob} disabled={mjTriggering}>
                    {mjTriggering ? 'Starting…' : 'Start Job'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Job-details modal */}
          {mjSel && (
            <div className="adm-overlay" onClick={() => setMjSelId(null)}>
              <div className="adm-dialog adm-dialog-wide adm-dialog-tall" onClick={e => e.stopPropagation()}>
                <div className="adm-dlg-hd">
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {JOB_TYPE_LABEL[mjSel.job_type] || mjSel.job_type}
                    <span className="adm-status" style={{ color: (JOB_STATUS_STYLE[mjSel.status] || JOB_STATUS_STYLE.STARTED).color, background: (JOB_STATUS_STYLE[mjSel.status] || JOB_STATUS_STYLE.STARTED).bg, borderColor: (JOB_STATUS_STYLE[mjSel.status] || JOB_STATUS_STYLE.STARTED).color }}>{mjSel.status}</span>
                  </h3>
                  <button className="adm-dlg-close" onClick={() => setMjSelId(null)}>✕</button>
                </div>
                <div className="adm-dlg-body" style={{ padding: 0 }}>
                  <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-3)', borderBottom: '1px solid var(--border)' }}>{jobSummary(mjSel)}</div>
                  {mjSel.error && <div style={{ padding: 12, color: 'var(--red)', fontSize: 12 }}>Error: {mjSel.error}</div>}
                  {mjSel.job_type === 'BACKFILL_HISTORICAL_QUOTES'
                    ? ((mjSel.per_symbol || []).length === 0
                        ? <div className="adm-empty-cell" style={{ padding: 24, textAlign: 'center' }}>No per-symbol results yet — the job may still be queued.</div>
                        : <div className="adm-table-wrap">
                            <table className="adm-table">
                              <thead><tr><th>Symbol</th><th>Status</th><th>Rows Written</th><th>Error</th></tr></thead>
                              <tbody>
                                {mjSel.per_symbol.map((p, i) => (
                                  <tr key={i}>
                                    <td><strong>{p.symbol}</strong></td>
                                    <td className={p.status === 'COMPLETED' ? 'up' : 'down'}>{p.status}</td>
                                    <td>{(p.rows_written || 0).toLocaleString('en-IN')}</td>
                                    <td style={{ fontSize: 11, color: 'var(--text-3)' }}>{p.error || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>)
                    : (mjSel.result
                        ? <div className="adm-table-wrap">
                            <table className="adm-table">
                              <thead><tr><th>Result</th><th>Value</th></tr></thead>
                              <tbody>
                                {Object.entries(mjSel.result).map(([k, v]) => (
                                  <tr key={k}><td><strong>{k}</strong></td><td>{String(v)}</td></tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        : <div className="adm-empty-cell" style={{ padding: 24, textAlign: 'center' }}>No result yet — the job is still running.</div>)
                  }
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
    );
  };


  // ── Trading Activity section ─────────────────────────────────────────────
  const renderTradingActivity = () => (
    <div className="adm-section">
      <div className="adm-section-hd">
        <h2>Trading Activity</h2>
        <p>View all users' paper trading accounts, orders, and P&amp;L across the platform.</p>
      </div>

      <div className="adm-table-bar" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {['accounts', 'orders'].map(t => (
            <button key={t} className={`adm-btn adm-btn-xs ${taTab === t ? 'adm-btn-accent' : 'adm-btn-ghost'}`}
              onClick={() => { setTaTab(t); updateTabParam(t); }}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {taTab === 'accounts' && (
            <>
              <input className="adm-search-input" placeholder="Search by name or email…"
                value={taSearch} onChange={e => setTaSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && fetchTradingAccounts(0, taSearch)}
                style={{ width: 200, fontSize: 12 }} />
              <button className="adm-btn adm-btn-ghost adm-btn-xs" onClick={() => fetchTradingAccounts(0, taSearch)}>Search</button>
              <button className="adm-btn adm-btn-accent adm-btn-xs" onClick={() => { setTaGrantForm({ email: '', cash: '1000000' }); setTaShowGrant(true); }}>+ Grant Tool</button>
            </>
          )}
          <span className="adm-pill">{taTotal} records</span>
          <button className="adm-btn adm-btn-ghost adm-btn-xs" title="Sell all open positions at the last price (runs automatically at 3:30 PM IST)"
            onClick={submitSquareOff} disabled={taActionLoading}>Square Off (EOD)</button>
          <button className="adm-btn adm-btn-ghost adm-btn-xs" onClick={() => taTab === 'accounts' ? fetchTradingAccounts(taPage) : fetchTradingOrders(taPage)}>Refresh</button>
        </div>
      </div>

      {taLoading
        ? <div className="adm-spin-wrap"><div className="adm-spinner" /></div>
        : taTab === 'accounts'
          ? <div className="adm-table-wrap">
            <table className="adm-table">
              <thead><tr><th>User</th><th>Email</th><th>Cash</th><th>Portfolio</th><th>P&amp;L</th><th>Since</th><th>Actions</th></tr></thead>
              <tbody>
                {taAccounts.length === 0
                  ? <tr><td colSpan={7} className="adm-empty-cell">No paper trading accounts yet.</td></tr>
                  : taAccounts.map(a => (
                    <tr key={a.id}>
                      <td>{a.user_name}</td>
                      <td className="adm-trunc" style={{ maxWidth: 180 }}>{a.user_email}</td>
                      <td>₹{Number(a.cash_balance).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                      <td>{a.total_value != null ? `₹${Number(a.total_value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—'}</td>
                      <td className={a.pnl >= 0 ? 'up' : 'down'}>{a.pnl != null ? `${a.pnl >= 0 ? '+' : ''}₹${Number(Math.abs(a.pnl)).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—'}</td>
                      <td className="adm-nowrap">{fmtDate(a.created_at)}</td>
                      <td>
                        <div className="adm-row-btns">
                          <button className="adm-icon-btn" title="View activity" onClick={() => openTaDetail(a.id)}>
                            <Icon paths={IC.view} size={14} />
                          </button>
                          <button className="adm-icon-btn" title="Add / adjust cash" onClick={() => { setTaTopupForm({ amount: '', note: '' }); setTaTopupTarget(a); }}>
                            <Icon paths="M12 5v14M5 12h14" size={14} />
                          </button>
                          <button className="adm-icon-btn adm-icon-btn-danger" title="Delete account" onClick={() => setTaDeleteTarget(a)}>
                            <Icon paths="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
          : <div className="adm-table-wrap">
            <table className="adm-table">
              <thead><tr><th>User</th><th>Symbol</th><th>Side</th><th>Qty</th><th>Price</th><th>Value</th><th>Status</th><th>Time</th></tr></thead>
              <tbody>
                {taOrders.length === 0
                  ? <tr><td colSpan={8} className="adm-empty-cell">No orders yet.</td></tr>
                  : taOrders.map(o => (
                    <tr key={o.id}>
                      <td className="adm-trunc" style={{ maxWidth: 160 }}>{o.user_email}</td>
                      <td><strong style={{ fontFamily: 'monospace', color: 'var(--accent)' }}>{o.symbol}</strong></td>
                      <td className={o.side === 'BUY' ? 'up' : 'down'}>{o.side}</td>
                      <td>{o.quantity}</td>
                      <td>{o.filled_price ? `₹${Number(o.filled_price).toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '—'}</td>
                      <td>{o.filled_price ? `₹${Number(o.filled_price * o.quantity).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—'}</td>
                      <td><span className={`adm-status ${o.status === 'FILLED' ? '' : ''}`} style={{ borderColor: o.status === 'FILLED' ? 'var(--green)' : o.status === 'PENDING' ? 'var(--amber)' : 'var(--text-3)', color: o.status === 'FILLED' ? 'var(--green)' : o.status === 'PENDING' ? 'var(--amber)' : 'var(--text-3)' }}>{o.status}</span></td>
                      <td className="adm-nowrap">{o.created_at ? new Date(o.created_at).toLocaleTimeString('en-IN') : '—'}</td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
      }

      {Math.ceil(taTotal / TA_LIMIT) > 1 && (
        <div className="adm-pager">
          <button className="adm-btn adm-btn-ghost adm-btn-xs" disabled={taPage === 0} onClick={() => taTab === 'accounts' ? fetchTradingAccounts(taPage - 1) : fetchTradingOrders(taPage - 1)}>← Prev</button>
          <span>Page {taPage + 1} of {Math.ceil(taTotal / TA_LIMIT)}</span>
          <button className="adm-btn adm-btn-ghost adm-btn-xs" disabled={taPage >= Math.ceil(taTotal / TA_LIMIT) - 1} onClick={() => taTab === 'accounts' ? fetchTradingAccounts(taPage + 1) : fetchTradingOrders(taPage + 1)}>Next →</button>
        </div>
      )}

      {/* ── Grant Paper Trading dialog ── */}
      {taShowGrant && (
        <div className="adm-overlay" onClick={() => !taActionLoading && setTaShowGrant(false)}>
          <div className="adm-dialog adm-dialog-sm" onClick={e => e.stopPropagation()}>
            <div className="adm-dlg-hd">
              <h3>Grant Paper Trading</h3>
              <button className="adm-dlg-close" onClick={() => setTaShowGrant(false)}>✕</button>
            </div>
            <div className="adm-dlg-body">
              <label className="adm-field">
                <span>User email</span>
                <input type="email" placeholder="user@example.com" autoFocus
                  value={taGrantForm.email}
                  onChange={e => setTaGrantForm(f => ({ ...f, email: e.target.value }))} />
              </label>
              <label className="adm-field">
                <span>Starting virtual cash (₹)</span>
                <input type="number" min="0" step="1000"
                  value={taGrantForm.cash}
                  onChange={e => setTaGrantForm(f => ({ ...f, cash: e.target.value }))} />
              </label>
              <p style={{ fontSize: 12, color: 'var(--text-3)', margin: 0 }}>
                Grants 30-day access to a registered user. If they already have an account, access is extended and the existing cash is kept.
              </p>
            </div>
            <div className="adm-dlg-ft">
              <button className="adm-btn adm-btn-ghost" onClick={() => setTaShowGrant(false)} disabled={taActionLoading}>Cancel</button>
              <button className="adm-btn adm-btn-accent" onClick={submitGrant} disabled={taActionLoading}>{taActionLoading ? 'Granting…' : 'Grant'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add / adjust cash dialog ── */}
      {taTopupTarget && (
        <div className="adm-overlay" onClick={() => !taActionLoading && setTaTopupTarget(null)}>
          <div className="adm-dialog adm-dialog-sm" onClick={e => e.stopPropagation()}>
            <div className="adm-dlg-hd">
              <h3>Adjust Cash — {taTopupTarget.user_name || taTopupTarget.user_email}</h3>
              <button className="adm-dlg-close" onClick={() => setTaTopupTarget(null)}>✕</button>
            </div>
            <div className="adm-dlg-body">
              <label className="adm-field">
                <span>Amount (₹) — negative removes cash</span>
                <input type="number" step="1000" placeholder="e.g. 100000" autoFocus
                  value={taTopupForm.amount}
                  onChange={e => setTaTopupForm(f => ({ ...f, amount: e.target.value }))} />
              </label>
              <label className="adm-field">
                <span>Note (optional)</span>
                <input type="text" placeholder="Reason for the adjustment"
                  value={taTopupForm.note}
                  onChange={e => setTaTopupForm(f => ({ ...f, note: e.target.value }))} />
              </label>
            </div>
            <div className="adm-dlg-ft">
              <button className="adm-btn adm-btn-ghost" onClick={() => setTaTopupTarget(null)} disabled={taActionLoading}>Cancel</button>
              <button className="adm-btn adm-btn-accent" onClick={submitTopup} disabled={taActionLoading}>{taActionLoading ? 'Saving…' : 'Apply'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete account confirm ── */}
      {taDeleteTarget && (
        <div className="adm-overlay" onClick={() => !taActionLoading && setTaDeleteTarget(null)}>
          <div className="adm-dialog adm-dialog-sm" onClick={e => e.stopPropagation()}>
            <div className="adm-dlg-hd">
              <h3>Delete Account</h3>
              <button className="adm-dlg-close" onClick={() => setTaDeleteTarget(null)}>✕</button>
            </div>
            <div className="adm-dlg-body">
              <p style={{ margin: 0, fontSize: 13 }}>
                Permanently delete the paper trading account of <strong>{taDeleteTarget.user_name || taDeleteTarget.user_email}</strong>?
                This removes all orders, positions, snapshots and cash history, and revokes their tool access. This cannot be undone.
              </p>
            </div>
            <div className="adm-dlg-ft">
              <button className="adm-btn adm-btn-ghost" onClick={() => setTaDeleteTarget(null)} disabled={taActionLoading}>Cancel</button>
              <button className="adm-btn adm-btn-danger" onClick={submitDelete} disabled={taActionLoading}>{taActionLoading ? 'Deleting…' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Account activity detail dialog ── */}
      {taViewId && (
        <div className="adm-overlay" onClick={closeTaDetail}>
          <div className="adm-dialog adm-dialog-wide adm-dialog-tall" onClick={e => e.stopPropagation()}>
            <div className="adm-dlg-hd">
              <h3>
                Activity — {taViewData?.account?.user_name || '…'}
                {taViewData?.account?.user_email && <span className="adm-tag" style={{ marginLeft: 8 }}>{taViewData.account.user_email}</span>}
              </h3>
              <button className="adm-dlg-close" onClick={closeTaDetail}>✕</button>
            </div>
            <div className="adm-dlg-body" style={{ gap: 18 }}>
              {taViewLoading || !taViewData
                ? <div className="adm-spin-wrap"><div className="adm-spinner" /></div>
                : <>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'flex-start' }}>
                      {[
                        ['Cash', `₹${Number(taViewData.account.cash_balance).toLocaleString('en-IN')}`],
                        ['Holdings', `₹${Number(taViewData.account.invested_value).toLocaleString('en-IN')}`],
                        ['Total Value', `₹${Number(taViewData.account.total_value).toLocaleString('en-IN')}`],
                        ['Deposited', `₹${Number(taViewData.account.total_deposited).toLocaleString('en-IN')}`],
                        ['P&L', `${taViewData.account.total_pnl >= 0 ? '+' : ''}₹${Number(taViewData.account.total_pnl).toLocaleString('en-IN')}`],
                      ].map(([lbl, val]) => (
                        <div key={lbl} style={{ minWidth: 110 }}>
                          <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', fontWeight: 700 }}>{lbl}</div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{val}</div>
                        </div>
                      ))}
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                        <button className="adm-btn adm-btn-accent adm-btn-xs" onClick={() => { setTaTopupForm({ amount: '', note: '' }); setTaTopupTarget(taViewData.account); }}>+ Add Cash</button>
                        <button className="adm-btn adm-btn-danger adm-btn-xs" onClick={() => setTaDeleteTarget(taViewData.account)}>Delete</button>
                      </div>
                    </div>

                    <div>
                      <h4 className="adm-card-title" style={{ marginBottom: 8 }}>Cash History</h4>
                      <div className="adm-table-wrap">
                        <table className="adm-table">
                          <thead><tr><th>Time (IST)</th><th>Type</th><th>Amount</th><th>Balance After</th><th>Note</th></tr></thead>
                          <tbody>
                            {taViewData.ledger.length === 0
                              ? <tr><td colSpan={5} className="adm-empty-cell">No cash entries.</td></tr>
                              : taViewData.ledger.map((l, i) => (
                                <tr key={i}>
                                  <td className="adm-nowrap" style={{ fontSize: 11 }}>{new Date(l.ts).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false })}</td>
                                  <td><span className="adm-tag">{l.type}</span></td>
                                  <td className={Number(l.amount) >= 0 ? 'up' : 'down'}>{Number(l.amount) >= 0 ? '+' : ''}₹{Number(Math.abs(l.amount)).toLocaleString('en-IN')}</td>
                                  <td>{l.balance_after != null ? `₹${Number(l.balance_after).toLocaleString('en-IN')}` : '—'}</td>
                                  <td className="adm-trunc" style={{ maxWidth: 240 }}>{l.note || '—'}</td>
                                </tr>
                              ))
                            }
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div>
                      <h4 className="adm-card-title" style={{ marginBottom: 8 }}>Current Holdings</h4>
                      <div className="adm-table-wrap">
                        <table className="adm-table">
                          <thead><tr><th>Symbol</th><th>Qty</th><th>Avg Buy</th><th>LTP</th><th>Value</th><th>Unrealised P&amp;L</th></tr></thead>
                          <tbody>
                            {taViewData.positions.filter(p => p.quantity > 0).length === 0
                              ? <tr><td colSpan={6} className="adm-empty-cell">No open positions.</td></tr>
                              : taViewData.positions.filter(p => p.quantity > 0).map(p => (
                                <tr key={p.symbol}>
                                  <td><strong>{p.symbol}</strong></td>
                                  <td>{p.quantity}</td>
                                  <td>₹{Number(p.avg_buy_price).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                                  <td>₹{Number(p.current_price).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                                  <td>₹{Number(p.current_value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                                  <td className={p.unrealised_pnl >= 0 ? 'up' : 'down'}>{p.unrealised_pnl >= 0 ? '+' : ''}₹{Number(Math.abs(p.unrealised_pnl)).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                                </tr>
                              ))
                            }
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div>
                      <h4 className="adm-card-title" style={{ marginBottom: 8 }}>Trades</h4>
                      <div className="adm-table-wrap">
                        <table className="adm-table">
                          <thead><tr><th>Time (IST)</th><th>Symbol</th><th>Side</th><th>Qty</th><th>Price</th><th>Value</th><th>Status</th></tr></thead>
                          <tbody>
                            {taViewData.orders.length === 0
                              ? <tr><td colSpan={7} className="adm-empty-cell">No trades.</td></tr>
                              : taViewData.orders.map(o => (
                                <tr key={o.id}>
                                  <td className="adm-nowrap" style={{ fontSize: 11 }}>{new Date(o.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false })}</td>
                                  <td><strong>{o.symbol}</strong></td>
                                  <td className={o.side === 'BUY' ? 'up' : 'down'}>{o.side}</td>
                                  <td>{o.quantity}</td>
                                  <td>{o.filled_price ? `₹${Number(o.filled_price).toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '—'}</td>
                                  <td>{o.total_value != null ? `₹${Number(o.total_value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—'}</td>
                                  <td><span className="adm-tag">{o.status}</span></td>
                                </tr>
                              ))
                            }
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ── renderTools ─────────────────────────────────────────────────────────────
  const renderTools = () => (
    <div className="adm-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Tools</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-3)' }}>Manage platform tools. Tools are seeded from the backend.</p>
        </div>
      </div>

      {tlLoading ? (
        <div className="adm-spin-wrap"><div className="adm-spinner" /></div>
      ) : (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead><tr>
              <th>#</th><th>Tool</th><th>Price</th><th>Type</th>
              <th>Active</th><th>Coming Soon</th><th>Actions</th>
            </tr></thead>
            <tbody>
              {toolList.length === 0 ? (
                <tr><td colSpan={7} className="adm-empty-cell">No tools found.</td></tr>
              ) : toolList.map(t => (
                <tr key={t._id}>
                  <td style={{ fontWeight: 600, color: 'var(--text-3)' }}>{t.tool_id}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <CourseIcon icon={t.icon} />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{t.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{t.tagline}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ fontWeight: 600 }}>{t.price === 0 ? 'Free' : `₹${t.price}`}</td>
                  <td><span className={`adm-tag ${t.is_paid ? 'adm-tag-amber' : 'adm-tag-green'}`}>{t.is_paid ? 'Paid' : 'Free'}</span></td>
                  <td><span className={`adm-tag ${t.is_active ? 'adm-tag-green' : 'adm-tag-red'}`}>{t.is_active ? 'Active' : 'Hidden'}</span></td>
                  <td><span className={`adm-tag ${t.is_coming_soon ? 'adm-tag-amber' : 'adm-tag-green'}`}>{t.is_coming_soon ? 'Soon' : 'Live'}</span></td>
                  <td>
                    <div className="adm-row-btns">
                      <button className="adm-icon-btn" title="View Details" onClick={() => setTlViewTarget(t)}>
                        <Icon paths="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" size={14} />
                      </button>
                      <button className="adm-icon-btn" title="Edit" onClick={() => openTlEdit(t)}>
                        <Icon paths={IC.edit} size={14} />
                      </button>
                      <button className="adm-icon-btn adm-icon-btn-danger" title="Delete" onClick={() => setTlDeleteTarget(t)}>
                        <Icon paths="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Tool dialog */}
      {tlShowForm && tlEditTarget && (
        <div className="adm-overlay" onClick={() => setTlShowForm(false)}>
          <div className="adm-dialog adm-dialog-wide" onClick={e => e.stopPropagation()}>
            <div className="adm-dlg-hd">
              <h3>Edit Tool — {tlEditTarget.name}</h3>
              <button className="adm-dlg-close" onClick={() => setTlShowForm(false)}>✕</button>
            </div>
            <form onSubmit={handleToolSave}>
              <div className="adm-dlg-body adm-cs-form">
                <div className="adm-cs-cols">
                  <div className="adm-cs-col">
                    <div className="adm-cs-section-title">Basic Info</div>
                    <label className="adm-field">
                      <span>Tool ID <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>— read-only</span></span>
                      <input value={tlEditTarget.tool_id || ''} readOnly style={{ opacity: 0.6, cursor: 'not-allowed' }} />
                    </label>
                    {[['Name *', 'name', 'text', 'e.g. Paper Trading'], ['Tagline', 'tagline', 'text', 'One-liner'], ['Icon', 'icon', 'text', 'e.g. bar-chart'], ['Price (₹)', 'price', 'number', '0']].map(([label, field, type, ph]) => (
                      <label key={field} className="adm-field">
                        <span>{label}</span>
                        <input type={type} value={tlForm[field]} placeholder={ph}
                          required={label.includes('*')}
                          min={type === 'number' ? 0 : undefined}
                          onChange={e => setTlForm(p => ({ ...p, [field]: e.target.value }))} />
                      </label>
                    ))}
                    <div style={{ display: 'flex', gap: 24, marginTop: 4, flexWrap: 'wrap' }}>
                      {[['is_paid', 'Paid tool'], ['is_active', 'Active (visible)'], ['is_coming_soon', 'Coming Soon']].map(([field, label]) => (
                        <label key={field} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 13, fontWeight: 400 }}>
                          <input type="checkbox" checked={!!tlForm[field]} onChange={e => setTlForm(p => ({ ...p, [field]: e.target.checked }))} style={{ width: 14, height: 14, accentColor: 'var(--accent)' }} />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="adm-cs-col">
                    <div className="adm-cs-section-title">Content</div>
                    <label className="adm-field">
                      <span>Description</span>
                      <textarea rows={4} value={tlForm.description} placeholder="Full description…"
                        onChange={e => setTlForm(p => ({ ...p, description: e.target.value }))} />
                    </label>
                    <div className="adm-cs-section-title" style={{ marginTop: 12 }}>Features</div>
                    {(tlForm.features || []).map((f, i) => (
                      <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                        <input style={{ flex: 1 }} value={f} placeholder={`Feature ${i + 1}`}
                          onChange={e => { const arr = [...tlForm.features]; arr[i] = e.target.value; setTlForm(p => ({ ...p, features: arr })); }} />
                        <button type="button" className="adm-icon-btn adm-icon-btn-danger" onClick={() => setTlForm(p => ({ ...p, features: p.features.filter((_, j) => j !== i) }))}>
                          <Icon paths="M18 6L6 18M6 6l12 12" size={12} />
                        </button>
                      </div>
                    ))}
                    <button type="button" className="adm-btn adm-btn-ghost adm-btn-xs" style={{ marginTop: 4 }}
                      onClick={() => setTlForm(p => ({ ...p, features: [...(p.features || []), ''] }))}>+ Add Feature</button>
                  </div>
                </div>
              </div>
              <div className="adm-dlg-ft">
                <button type="button" className="adm-btn adm-btn-ghost" onClick={() => setTlShowForm(false)}>Cancel</button>
                <button type="submit" className="adm-btn adm-btn-accent" disabled={tlSaving}>{tlSaving ? 'Saving…' : 'Save Tool'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {tlDeleteTarget && (
        <div className="adm-overlay" onClick={() => setTlDeleteTarget(null)}>
          <div className="adm-dialog" onClick={e => e.stopPropagation()}>
            <div className="adm-dlg-hd"><h3>Delete Tool</h3><button className="adm-dlg-close" onClick={() => setTlDeleteTarget(null)}>✕</button></div>
            <div className="adm-dlg-body">
              <p style={{ margin: 0 }}>Delete <strong>{tlDeleteTarget.name}</strong>? This cannot be undone.</p>
            </div>
            <div className="adm-dlg-ft">
              <button className="adm-btn adm-btn-ghost" onClick={() => setTlDeleteTarget(null)}>Cancel</button>
              <button className="adm-btn adm-btn-danger" onClick={handleToolDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Tool detail view */}
      {tlViewTarget && (() => {
        const t = tlViewTarget;
        return (
          <div className="adm-overlay" onClick={() => setTlViewTarget(null)}>
            <div className="adm-dialog adm-dialog-wide adm-dialog-tall" onClick={e => e.stopPropagation()}>
              <div className="adm-dlg-hd">
                <h3 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <CourseIcon icon={t.icon} />
                  {t.name}
                  <span className={`adm-tag ${t.is_paid ? 'adm-tag-amber' : 'adm-tag-green'}`} style={{ marginLeft: 4 }}>{t.is_paid ? 'Paid' : 'Free'}</span>
                </h3>
                <button className="adm-dlg-close" onClick={() => setTlViewTarget(null)}>✕</button>
              </div>
              <div className="adm-dlg-body" style={{ gap: 18 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                  {[['Tool ID', t.tool_id], ['Price', t.price === 0 ? 'Free' : `₹${t.price}`], ['Active', t.is_active ? '✓ Yes' : '✗ No'], ['Coming Soon', t.is_coming_soon ? '✓ Yes' : '✗ No']]
                    .map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 90 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)' }}>{k}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{v}</span>
                      </div>
                    ))}
                </div>
                {t.tagline && <p style={{ fontSize: 14, color: 'var(--text-2)', fontStyle: 'italic', margin: 0 }}>{t.tagline}</p>}
                {t.description && (
                  <div>
                    <div className="adm-cs-section-title" style={{ marginBottom: 6 }}>Description</div>
                    <p style={{ fontSize: 13, color: 'var(--text-2)', margin: 0, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{t.description}</p>
                  </div>
                )}
                {(t.features || []).length > 0 && (
                  <div>
                    <div className="adm-cs-section-title" style={{ marginBottom: 8 }}>Features</div>
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {t.features.map((f, i) => (
                        <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: 'var(--text-2)' }}>
                          <span style={{ color: 'var(--accent)', marginTop: 2 }}>✓</span> {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div className="adm-dlg-ft">
                <button className="adm-btn adm-btn-ghost" onClick={() => setTlViewTarget(null)}>Close</button>
                <button className="adm-btn adm-btn-accent" onClick={() => { setTlViewTarget(null); openTlEdit(t); }}>Edit</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':        return renderDashboard();
      case 'contactmessages':  return renderMessages();
      case 'blogs':            return <div className="adm-section"><BlogManager /></div>;
      case 'users':            return renderUsers();
      case 'adminusers':       return renderAdminUsers();
      case 'profile':          return renderProfile();
      case 'courses':          return renderCourses();
      case 'tools':            return renderTools();
      case 'payments':         return renderComingSoon('Payments & Revenue', 'Reconcile Razorpay orders, view subscription revenue, and manage refunds.');
      case 'stocks':           return renderStocks();
      case 'marketdata':       return renderMarketData();
      case 'tradingactivity':  return renderTradingActivity();
      default:                 return renderDashboard();
    }
  };

  const SECTION_LABELS = {
    dashboard: 'Dashboard', contactmessages: 'Contact Messages', blogs: 'Blogs',
    users: 'Normal Users', adminusers: 'Admin Users', courses: 'Courses', tools: 'Tools',
    payments: 'Payments', profile: 'My Profile', stocks: 'Stocks', marketdata: 'Market Data', tradingactivity: 'Trading Activity',
  };
  const activeMeta = (() => {
    for (const s of SECTIONS) {
      if (!s.group && s.id === activeSection) return s;
      if (s.group) { const c = s.children?.find(ch => ch.id === activeSection); if (c) return c; }
    }
    return { label: SECTION_LABELS[activeSection] || 'Dashboard' };
  })();

  // ── JSX ────────────────────────────────────────────────────────────────────
  return (
    <div className={`adm-shell${collapsed ? ' adm-collapsed' : ''}`}>
      <Seo title="Admin Dashboard — InvFly" description="InvFly administration." noIndex />

      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      <aside className="adm-sidebar" role="navigation" aria-label="Admin navigation">

        {/* Brand */}
        <div className="adm-brand">
          <img src={logo} alt="InvFly" style={{ height: 34, width: 'auto', objectFit: 'contain', filter: 'brightness(0) invert(1)', opacity: 0.92 }} />
          <div className="adm-brand-text">
            <span className="adm-brand-name">InvFly</span>
            <span className="adm-brand-sub">Admin Platform</span>
          </div>
        </div>

        {/* Nav items */}
        <nav className="adm-nav">
          {visibleSections.map(s => {
            if (s.group) {
              const isExpanded = expandedGroups[s.id];
              const groupActive = s.children.some(c => c.id === activeSection);
              return (
                <div key={s.id} className="adm-nav-group">
                  <button
                    className={`adm-nav-btn adm-nav-group-hd${groupActive ? ' active' : ''}`}
                    onClick={() => toggleGroup(s.id)}
                    data-tooltip={s.label}
                  >
                    <span className="adm-nav-icon"><Icon paths={s.icon} size={18} /></span>
                    <span className="adm-nav-label">{s.label}</span>
                    <span className="adm-nav-chevron">
                      <Icon paths={isExpanded ? IC.chevronU : IC.chevronD} size={12} />
                    </span>
                  </button>
                  {isExpanded && s.children.map(child => (
                    <button key={child.id}
                      className={`adm-nav-btn adm-nav-child${activeSection === child.id ? ' active' : ''}`}
                      onClick={() => goSection(child.id)}
                      data-tooltip={child.label}
                      aria-current={activeSection === child.id ? 'page' : undefined}
                    >
                      <span className="adm-nav-icon"><Icon paths={child.icon} size={16} /></span>
                      <span className="adm-nav-label">{child.label}</span>
                    </button>
                  ))}
                </div>
              );
            }
            return (
              <button key={s.id}
                className={`adm-nav-btn${activeSection === s.id ? ' active' : ''}`}
                onClick={() => goSection(s.id)}
                data-tooltip={s.label}
                aria-current={activeSection === s.id ? 'page' : undefined}
              >
                <span className="adm-nav-icon"><Icon paths={s.icon} size={18} /></span>
                <span className="adm-nav-label">{s.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Sidebar footer */}
        <div className="adm-sidebar-foot">
          <button className="adm-nav-btn adm-nav-logout" onClick={logout} data-tooltip="Sign out">
            <span className="adm-nav-icon"><Icon paths={IC.logout} size={18} /></span>
            <span className="adm-nav-label">Sign out</span>
          </button>
          <button className="adm-collapse-btn" onClick={() => setCollapsed(c => !c)} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} data-tooltip={collapsed ? 'Expand' : 'Collapse'}>
            <Icon paths={collapsed ? IC.chevronR : IC.chevronL} size={16} />
          </button>
        </div>
      </aside>

      {/* ── Main ────────────────────────────────────────────────────────── */}
      <div className="adm-body">

        {/* Topbar */}
        <header className="adm-topbar">
          <button className="adm-burger" onClick={() => setCollapsed(c => !c)} aria-label="Toggle sidebar">
            <Icon paths={IC.menu} size={20} />
          </button>

          <div className="adm-topbar-heading">
            <span className="adm-topbar-title">{activeMeta.label}</span>
          </div>

          <div className="adm-topbar-end" ref={userMenuRef}>
            {setTheme && (
              <button
                className="adm-theme-toggle"
                onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}
                title="Toggle theme"
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <circle cx="12" cy="12" r="5"/>
                    <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                  </svg>
                )}
              </button>
            )}
            <button className="adm-user-pill" onClick={() => setUserMenuOpen(o => !o)}>
              <span className="adm-avatar-sm">{initials(adminInfo?.name || adminInfo?.email)}</span>
              <span className="adm-user-pill-name">{adminInfo?.name || adminInfo?.email?.split('@')[0] || 'Admin'}</span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
                <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {userMenuOpen && (
              <div className="adm-user-dd" role="menu">
                <div className="adm-user-dd-hd">
                  <span className="adm-avatar-md">{initials(adminInfo?.name || adminInfo?.email)}</span>
                  <div>
                    <div className="adm-user-dd-name">{adminInfo?.name || adminInfo?.email?.split('@')[0] || 'Admin'}</div>
                    <div className="adm-user-dd-email">{adminInfo?.email || ''}</div>
                    <span className="adm-tag adm-tag-accent" style={{ fontSize: 10, marginTop: 4, display: 'inline-block' }}>{((adminInfo?.roles || [])[0] || 'superadmin').toUpperCase()}</span>
                  </div>
                </div>
                <div className="adm-user-dd-sep" />
                <button className="adm-user-dd-item" onClick={() => goSection('profile')} role="menuitem">
                  <Icon paths={IC.profile} size={14} /> My Profile
                </button>
                <div className="adm-user-dd-sep" />
                <button className="adm-user-dd-item adm-user-dd-logout" onClick={logout} role="menuitem">
                  <Icon paths={IC.logout} size={14} /> Sign out
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Content */}
        <main className="adm-content">
          {renderContent()}
        </main>
      </div>

      {/* ── View message dialog ──────────────────────────────────────────── */}
      {viewMsg && (
        <div className="adm-overlay" onClick={() => setViewMsg(null)}>
          <div className="adm-dialog" onClick={e => e.stopPropagation()}>
            <div className="adm-dlg-hd"><h3>Message Details</h3><button className="adm-dlg-close" onClick={() => setViewMsg(null)}>✕</button></div>
            <div className="adm-dlg-body">
              {[['Name', viewMsg.name], ['Email', viewMsg.email], ['Phone', viewMsg.phone], ['Subject', viewMsg.subject]].filter(([, v]) => v).map(([l, v]) => (
                <div key={l} className="adm-drow"><span>{l}</span><strong>{v}</strong></div>
              ))}
              <div className="adm-drow adm-drow-block"><span>Message</span><div className="adm-msg-box">{viewMsg.message}</div></div>
              <div className="adm-drow">
                <span>Status</span>
                <span className="adm-status" style={{ borderColor: STATUS_COLORS[viewMsg.status || 'new'], color: STATUS_COLORS[viewMsg.status || 'new'] }}>{(viewMsg.status || 'new').toUpperCase()}</span>
              </div>
              <div className="adm-drow"><span>Submitted</span><strong>{fmtDate(viewMsg.created_at)}</strong></div>
            </div>
            <div className="adm-dlg-ft"><button className="adm-btn adm-btn-ghost" onClick={() => setViewMsg(null)}>Close</button></div>
          </div>
        </div>
      )}

      {/* ── Edit status dialog ───────────────────────────────────────────── */}
      {editMsg && (
        <div className="adm-overlay" onClick={() => setEditMsg(null)}>
          <div className="adm-dialog adm-dialog-sm" onClick={e => e.stopPropagation()}>
            <div className="adm-dlg-hd"><h3>Update Status</h3><button className="adm-dlg-close" onClick={() => setEditMsg(null)}>✕</button></div>
            <div className="adm-dlg-body">
              <div className="adm-drow"><span>Name</span><strong>{editMsg.name}</strong></div>
              <div className="adm-drow"><span>Email</span><strong>{editMsg.email}</strong></div>
              <label className="adm-field adm-field-lg" style={{ marginTop: 8 }}>
                <span>Status</span>
                <select value={editStatus} onChange={e => setEditStatus(e.target.value)}>
                  {CONTACT_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                </select>
              </label>
            </div>
            <div className="adm-dlg-ft">
              <button className="adm-btn adm-btn-ghost" onClick={() => setEditMsg(null)}>Cancel</button>
              <button className="adm-btn adm-btn-accent" onClick={handleStatusUpdate}>Update</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Global Toast ─────────────────────────────────────────────────── */}
      <Toast
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ message: '', type: 'error' })}
      />
    </div>
  );
}
